/**
 * Global File Upload Hook with Multipart Support
 *
 * This hook provides comprehensive file upload functionality with automatic strategy selection:
 *
 * SINGLE UPLOAD:
 * - For files < 50MB or when uploading ≤ 5 files
 * - Uses direct signed URL upload
 * - Simpler and faster for small files
 *
 * MULTIPART UPLOAD:
 * - For files ≥ 50MB or when uploading > 5 files
 * - Splits files into 5MB chunks
 * - Uploads chunks in parallel with retry logic
 * - Provides detailed progress tracking per chunk
 * - Handles ETag collection for completion
 *
 * FILE VALIDATION:
 * - Maximum file size: 1TB per file
 * - Maximum file count: 100 files per upload
 * - Files exceeding limits are rejected with clear error messages
 * - Validation occurs before upload processing begins
 *
 * FEATURES:
 * - Automatic strategy selection based on file size and count
 * - Progress tracking with chunk-level details
 * - Retry logic with exponential backoff
 * - Error handling and recovery
 * - Visual feedback in upload progress component
 * - Comprehensive file validation with user-friendly error messages
 *
 * API ENDPOINTS USED:
 * - POST /files/upload/multipart/start - Initialize multipart upload
 * - POST /files/upload/multipart/get-urls - Get signed URLs for chunks
 * - PATCH /files/upload/multipart/complete - Complete multipart upload
 *
 * MODERN REACT QUERY HOOKS:
 *
 * // For single file uploads to signed URLs
 * const uploadMutation = useUploadToSignedUrl()
 *
 * uploadMutation.mutate({
 *   signedUrl: 'https://s3.amazonaws.com/...',
 *   file: fileObject,
 *   onProgress: (progress) => console.log(`Upload progress: ${progress}%`)
 * })
 *
 * // For chunk uploads (multipart)
 * const chunkUploadMutation = useUploadChunkToSignedUrl()
 *
 * chunkUploadMutation.mutate({
 *   signedUrl: 'https://s3.amazonaws.com/...',
 *   chunk: blobChunk,
 *   onProgress: (progress) => console.log(`Chunk progress: ${progress}%`)
 * })
 *
 * // Check upload status
 * if (uploadMutation.isPending) console.log('Uploading...')
 * if (uploadMutation.isSuccess) console.log('Upload complete!')
 * if (uploadMutation.isError) console.log('Upload failed:', uploadMutation.error)
 */

// Locally extend the DTOs to allow parentPath
import type {
  CompleteMultipartUploadDto,
  CompleteSingleUploadDto,
  MediaType,
  ResumeMultipartUploadUrlDto,
  StartMultipartUploadUrlDto as OrigStartMultipartUploadUrlDto,
  StartSingleUploadResponseDto,
  StartSingleUploadUrlDto as OrigStartSingleUploadUrlDto,
} from '@qse/api'
import {
  completeMultipartUpload,
  completeSingleUpload,
  FileStatus,
  getMultipartFileSignedUrls,
  startMultipartUpload,
  startSingleUpload,
} from '@qse/api'
import { queryKeys } from '@qse/api'
import { abortFileUpload, bulkAbortUploads } from '@qse/api'
import { FolderUploadProgressPayload } from '@qse/constants'
import { shouldSkipCacheUpdatesWithLog } from '@qse/utils'
import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import { create } from 'zustand'

import { EXTENSION_TO_MIME, getMediaTypeByExtension } from '../file-type'
import { FileItem } from './useFileManagment'
import { UploadingFile } from './useFileUpload'
import { findMatchingPath, FolderInit } from './useFolderUpload.helpers'

// Utility function to check if a file should be filtered out (hidden or system files)
const shouldFilterFile = (fileName: string): boolean => {
  const isHiddenFile = fileName.startsWith('.')
  const isSystemFile = fileName === 'Thumbs.db' || fileName === 'desktop.ini'

  return isHiddenFile || isSystemFile
}

interface StartSingleUploadUrlDtoWithPath extends OrigStartSingleUploadUrlDto {
  parentPath?: string
}
interface StartMultipartUploadUrlDtoWithPath extends OrigStartMultipartUploadUrlDto {
  parentPath?: string
}

type StartUploadRequest = StartSingleUploadUrlDtoWithPath
type StartUploadResponse = StartSingleUploadResponseDto
type CompleteUploadRequest = CompleteSingleUploadDto

type GetMultipartUrlsRequest = ResumeMultipartUploadUrlDto
type CompleteMultipartRequest = CompleteMultipartUploadDto

// Configuration constants - Ultra-optimized for performance
const MULTIPART_THRESHOLD = 5 * 1024 * 1024 // 5MB - use multipart for smaller files
const CHUNK_SIZE = 5 * 1024 * 1024 // 50MB per chunk - much larger chunks
const MAX_CONCURRENT_CHUNKS = 5 // More concurrent uploads
const MAX_SINGLE_FILES = 3 // Lower threshold for multipart
const BATCH_SIZE = 5 // Upload 5 files at a time to prevent browser hang-ups
const BATCH_DELAY = 1000 // 1 second delay between batches to prevent overwhelming the browser
const MAX_GLOBAL_CONCURRENT_UPLOADS = 10 // Global limit for concurrent uploads (folders + files)

// File validation constants
const MAX_FILE_SIZE = 1024 * 1024 * 1024 * 1024 * 1024 // 1TB in bytes
const MAX_FILE_COUNT = 100 // Maximum number of files allowed per upload

// Upload persistence constants
const UPLOAD_STORAGE_KEY = 'qse_active_uploads'
const TAB_CHECK_TIMEOUT = 2000 // 2 seconds to wait for other tabs to respond
const STALE_UPLOAD_MAX_AGE = 2 * 24 * 60 * 60 * 1000 // 2 days in milliseconds

// Upload persistence types
interface StoredUpload {
  id: string // serverFileId or folderUploadId
  type: 'file' | 'folder'
  timestamp: number
  localFileId?: number // For reference
}

// BroadcastChannel for multi-tab coordination
let uploadBroadcastChannel: BroadcastChannel | null = null
const tabId: string = `tab_${Date.now()}_${Math.random()}` // Unique tab identifier

// Flag to prevent duplicate stale upload checks
let isCheckingStaleUploads = false

// Failed file details for folder uploads
export interface FailedFileDetail {
  name: string
  path: string
  reason?: 'upload' | 'encryption' | 'deleted_destination' | 'zero_bytes'
  file?: File // [RETRY_FAILED] Original File object retained for retry
  parentId?: string // [RETRY_FAILED] Parent folder ID for upload target
}

// Helper function to add or update failed file details without duplicates
function addOrUpdateFailedFileDetail(
  existingDetails: FailedFileDetail[] = [],
  newDetail: FailedFileDetail,
): FailedFileDetail[] {
  // Check if file already exists in failed details
  const existingIndex = existingDetails.findIndex(
    detail => detail.name === newDetail.name && detail.path === newDetail.path,
  )

  if (existingIndex >= 0) {
    // Update existing entry with new reason (most recent failure reason)
    const updatedDetails = [...existingDetails]

    updatedDetails[existingIndex] = newDetail

    return updatedDetails
  } else {
    // Add new entry
    return [...existingDetails, newDetail]
  }
}

// ============================================================================
// HELPER FUNCTIONS - Upload Persistence
// ============================================================================

/**
 * Get BroadcastChannel instance for multi-tab coordination
 */
const getUploadBroadcastChannel = (): BroadcastChannel | null => {
  if (!uploadBroadcastChannel && typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    uploadBroadcastChannel = new BroadcastChannel('qse_upload_channel')
  }

  return uploadBroadcastChannel
}

/**
 * Get active upload IDs from current tab state
 */
const getActiveUploadIds = (uploadingFiles: EnhancedUploadingFile[]): Set<string> => {
  return new Set(
    uploadingFiles
      .filter(f => f.status === FileStatus.UPLOADING)
      .map(f => (f.isFolderUpload ? f.folderUploadId! : f.serverFileId!))
      .filter(Boolean),
  )
}

/**
 * Get stored uploads from localStorage
 */
const getStoredUploads = (): StoredUpload[] => {
  try {
    const stored = localStorage.getItem(UPLOAD_STORAGE_KEY)

    if (!stored) return []

    return JSON.parse(stored)
  } catch {
    return []
  }
}

/**
 * Save uploads to localStorage and broadcast to other tabs
 */
const saveUploadingFilesToStorage = (uploadingFiles: EnhancedUploadingFile[]) => {
  try {
    const storedUploads: StoredUpload[] = uploadingFiles
      .filter(f => {
        const hasServerId = f.serverFileId || (f.isFolderUpload && f.folderUploadId)

        return hasServerId && f.status === FileStatus.UPLOADING
      })
      .map(f => ({
        id: f.isFolderUpload ? f.folderUploadId! : f.serverFileId!,
        type: f.isFolderUpload ? 'folder' : 'file',
        timestamp: Date.now(),
        localFileId: f.id,
      }))

    localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(storedUploads))

    // Broadcast to other tabs
    const channel = getUploadBroadcastChannel()

    if (channel) {
      channel.postMessage({
        type: 'UPLOADS_UPDATED',
        tabId,
        uploads: storedUploads,
      })
    }
  } catch (error) {
    console.error('Failed to save uploading files to storage:', error)
  }
}

/**
 * Remove upload from localStorage and broadcast to other tabs
 */
const removeUploadFromStorage = (id: string, type: 'file' | 'folder') => {
  try {
    const stored = localStorage.getItem(UPLOAD_STORAGE_KEY)

    if (!stored) return

    const uploads: StoredUpload[] = JSON.parse(stored)
    const filtered = uploads.filter(u => !(u.id === id && u.type === type))

    if (filtered.length === 0) {
      localStorage.removeItem(UPLOAD_STORAGE_KEY)
    } else {
      localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(filtered))
    }

    // Broadcast to other tabs
    const channel = getUploadBroadcastChannel()

    if (channel) {
      channel.postMessage({
        type: 'UPLOAD_COMPLETED',
        tabId,
        id,
        uploadType: type,
      })
    }
  } catch (error) {
    console.error('Failed to remove upload from storage:', error)
  }
}

/**
 * Clean up localStorage to only include relevant uploads
 */
const cleanupStorage = (activeUploadIds: Set<string>, otherTabsActiveIds: Set<string>) => {
  try {
    const stored = localStorage.getItem(UPLOAD_STORAGE_KEY)

    if (!stored) return

    const uploads: StoredUpload[] = JSON.parse(stored)
    const relevantUploads = uploads.filter(
      u => activeUploadIds.has(u.id) || otherTabsActiveIds.has(u.id),
    )

    if (relevantUploads.length === 0) {
      localStorage.removeItem(UPLOAD_STORAGE_KEY)
    } else {
      localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(relevantUploads))
    }
  } catch (error) {
    console.error('Failed to cleanup storage:', error)
  }
}

/**
 * Check if other tabs are actively uploading these files
 */
const checkIfOtherTabsAreUploading = (
  uploadIds: Array<{ id: string; type: 'file' | 'folder' }>,
): Promise<Set<string>> => {
  return new Promise(resolve => {
    const channel = getUploadBroadcastChannel()

    if (!channel) {
      resolve(new Set())

      return
    }

    const activeUploadIds = new Set<string>()
    let responseTimeout: NodeJS.Timeout | null = null

    const cleanup = () => {
      if (responseTimeout) {
        clearTimeout(responseTimeout)
        responseTimeout = null
      }
      channel.removeEventListener('message', handleResponse)
    }

    const handleResponse = (event: MessageEvent) => {
      if (event.data.tabId === tabId) return

      if (event.data.type === 'UPLOADING_CHECK_RESPONSE') {
        event.data.uploadIds.forEach((id: string) => activeUploadIds.add(id))
      } else if (event.data.type === 'UPLOADS_UPDATED') {
        event.data.uploads.forEach((upload: StoredUpload) => {
          const matches = uploadIds.some(
            check => check.id === upload.id && check.type === upload.type,
          )

          if (matches) {
            activeUploadIds.add(upload.id)
          }
        })
      }
    }

    channel.addEventListener('message', handleResponse)

    // Broadcast check request
    channel.postMessage({
      type: 'UPLOADING_CHECK_REQUEST',
      tabId,
      uploadIds,
    })

    // Set timeout to resolve after waiting for responses
    responseTimeout = setTimeout(() => {
      cleanup()
      resolve(activeUploadIds)
    }, TAB_CHECK_TIMEOUT)
  })
}

/**
 * Update query cache after canceling an upload
 */
const updateQueryCacheAfterCancel = (
  queryClient: any,
  uploadId: string,
  uploadType: 'file' | 'folder',
) => {
  if (!queryClient) return

  const filesListKey = queryKeys.files.lists()
  const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

  queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
    if (!cacheData) return

    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData

      // Handle paginated data
      if (oldData.pages) {
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: (page.data || [])
              .map((f: any) => {
                if (f.id === uploadId) {
                  return uploadType === 'folder' ? { ...f, status: FileStatus.AVAILABLE } : null
                }

                return f
              })
              .filter(Boolean),
          })),
        }
      }

      // Handle non-paginated data
      if (oldData.data) {
        const updatedData = (oldData.data || [])
          .map((f: any) => {
            if (f.id === uploadId) {
              return uploadType === 'folder' ? { ...f, status: FileStatus.AVAILABLE } : null
            }

            return f
          })
          .filter(Boolean)

        return {
          ...oldData,
          data: updatedData,
          totalCount: Math.max(0, (oldData.totalCount || 0) - (uploadType === 'file' ? 1 : 0)),
        }
      }

      return oldData
    })
  })
}

/**
 * Cancel a single stale upload
 */
const cancelStaleUpload = async (upload: StoredUpload, queryClient?: any): Promise<void> => {
  // Check if upload is older than 2 days - let cronjob handle it
  const uploadAge = Date.now() - upload.timestamp

  if (uploadAge > STALE_UPLOAD_MAX_AGE) {
    // Upload is too old, just remove from storage (cronjob will handle it)
    removeUploadFromStorage(upload.id, upload.type)

    return
  }

  try {
    const { abortFileUpload, cancelFolderUpload } = await import('@qse/api')

    if (upload.type === 'folder') {
      await cancelFolderUpload(upload.id)
    } else {
      await abortFileUpload(upload.id)
    }

    // Update query cache
    if (queryClient) {
      updateQueryCacheAfterCancel(queryClient, upload.id, upload.type)
    }

    // Remove from storage after successful cancellation
    removeUploadFromStorage(upload.id, upload.type)
  } catch (error) {
    // Even if cancellation fails, remove from storage if it's clearly stale
    console.error(`Failed to cancel stale ${upload.type} upload ${upload.id}:`, error)
    removeUploadFromStorage(upload.id, upload.type)
  }
}

/**
 * Check and cancel stale uploads on page load
 */
const checkAndCancelStaleUploads = async (
  getState: () => GlobalFileUploadState,
  queryClient?: any,
) => {
  // Prevent duplicate executions
  if (isCheckingStaleUploads) return
  isCheckingStaleUploads = true

  try {
    // Get stored uploads from localStorage
    const storedUploads = getStoredUploads()

    if (storedUploads.length === 0) return

    // Get active uploads from current tab
    const { uploadingFiles } = getState()
    const currentTabUploadIds = getActiveUploadIds(uploadingFiles)

    // Filter out uploads that are active in current tab
    const uploadsNotInCurrentTab = storedUploads.filter(u => !currentTabUploadIds.has(u.id))

    if (uploadsNotInCurrentTab.length === 0) return

    // Check if any other tabs are actively uploading these files
    const uploadIds = uploadsNotInCurrentTab.map(u => ({ id: u.id, type: u.type }))
    const otherTabsActiveIds = await checkIfOtherTabsAreUploading(uploadIds)

    // Filter out uploads that are still active in other tabs
    const staleUploads = uploadsNotInCurrentTab.filter(u => !otherTabsActiveIds.has(u.id))

    if (staleUploads.length === 0) {
      // Clean up storage to only include active uploads
      cleanupStorage(currentTabUploadIds, otherTabsActiveIds)

      return
    }

    const folderStaleUploads = staleUploads.filter(upload => upload.type === 'folder')
    const fileStaleUploads = staleUploads.filter(upload => upload.type === 'file')

    if (fileStaleUploads.length > 0) {
      const fileIds = fileStaleUploads.map(upload => upload.id)

      try {
        const { bulkAbortUploads } = await import('@qse/api')

        await bulkAbortUploads({ fileIds })

        if (queryClient) {
          fileStaleUploads.forEach(upload => {
            updateQueryCacheAfterCancel(queryClient, upload.id, 'file')
          })
        }

        fileStaleUploads.forEach(upload => {
          removeUploadFromStorage(upload.id, 'file')
        })
      } catch (error) {
        console.error(
          'Failed to bulk abort stale file uploads, falling back to individual abort:',
          error,
        )
        await Promise.all(fileStaleUploads.map(upload => cancelStaleUpload(upload, queryClient)))
      }
    }

    if (folderStaleUploads.length > 0) {
      await Promise.all(
        folderStaleUploads.map(async upload => {
          try {
            const { cancelFolderUpload } = await import('@qse/api')

            await cancelFolderUpload(upload.id)

            if (queryClient) {
              updateQueryCacheAfterCancel(queryClient, upload.id, 'folder')
            }

            removeUploadFromStorage(upload.id, 'folder')
          } catch (error) {
            console.error(`Failed to cancel stale folder upload ${upload.id}:`, error)
            removeUploadFromStorage(upload.id, 'folder')
          }
        }),
      )
    }

    // Final cleanup after cancellation
    const { uploadingFiles: finalFiles } = getState()
    const finalActiveIds = getActiveUploadIds(finalFiles)

    cleanupStorage(finalActiveIds, otherTabsActiveIds)
  } catch (error) {
    console.error('Failed to check and cancel stale uploads:', error)
  } finally {
    isCheckingStaleUploads = false
  }
}

/**
 * Setup BroadcastChannel listener to respond to upload checks
 */
const setupBroadcastChannelListener = (
  getState: () => GlobalFileUploadState,
  setState: (
    state:
      | Partial<GlobalFileUploadState>
      | ((state: GlobalFileUploadState) => GlobalFileUploadState),
  ) => void,
) => {
  const channel = getUploadBroadcastChannel()

  if (!channel) return

  channel.onmessage = (event: MessageEvent) => {
    if (event.data.tabId === tabId) return

    if (event.data.type === 'UPLOADING_CHECK_REQUEST') {
      // Another tab is checking if we're uploading these files
      const { uploadIds } = event.data
      const { uploadingFiles } = getState()

      const activeUploadIds = uploadIds
        .filter((check: { id: string; type: 'file' | 'folder' }) => {
          return uploadingFiles.some((f: EnhancedUploadingFile) => {
            if (f.status !== FileStatus.UPLOADING) return false
            if (check.type === 'folder') {
              return f.isFolderUpload && f.folderUploadId === check.id
            } else {
              return !f.isFolderUpload && f.serverFileId === check.id
            }
          })
        })
        .map((check: { id: string; type: 'file' | 'folder' }) => check.id)

      // Respond with which uploads we're actively handling
      if (activeUploadIds.length > 0) {
        channel.postMessage({
          type: 'UPLOADING_CHECK_RESPONSE',
          tabId,
          uploadIds: activeUploadIds,
        })
      }
    } else if (event.data.type === 'UPLOAD_COMPLETED') {
      // Another tab completed an upload - remove from our local state if it exists
      const { id, uploadType } = event.data
      const { uploadingFiles } = getState()

      const file = uploadingFiles.find((f: EnhancedUploadingFile) => {
        if (uploadType === 'folder') {
          return f.isFolderUpload && f.folderUploadId === id
        } else {
          return !f.isFolderUpload && f.serverFileId === id
        }
      })

      if (file) {
        setState(state => ({
          ...state,
          uploadingFiles: state.uploadingFiles.filter(f => f.id !== file.id),
        }))
      }
    }
  }
}

// Enhanced UploadingFile type with optimized progress tracking
interface EnhancedUploadingFile extends UploadingFile {
  originalFile?: File
  originalName?: string // Original filename from browser before backend processing
  serverName?: string // Server-assigned name (for naming conflicts)
  uploadStrategy?: 'single' | 'multipart' | 'folder'
  clientRequestId?: string // Client-generated ID used before server responds
  uploadedBytes?: number
  totalBytes?: number
  speed?: number
  eta?: number
  lastProgressUpdate?: number
  serverFileId?: string // Store the server file ID for abort operations
  parentId?: string
  parentPath?: string
  encryptionStartTime?: number // Track when encryption started for minimum duration
  // Batch upload specific properties
  singleUrl?: string
  parts?: Array<{
    PartNumber: number
    url: string
  }>
  uploadType?: 'SINGLE' | 'MULTIPART'
  batchIndex?: number // Track which batch this file belongs to
  // Folder upload specific properties
  isFolderUpload?: boolean
  totalFiles?: number
  uploadedFiles?: number
  failedFiles?: number
  failedUploads?: number // Files that failed during upload phase
  encryptedFiles?: number // Files that completed encryption
  failedEncryptions?: number // Files that failed during encryption phase
  folderUploadId?: string
  failedFileDetails?: FailedFileDetail[] // Detailed list of failed files with paths
  pathToId?: Map<string, string> // Store folder structure mapping for nested deletion detection
  _perFileLoaded?: Record<string, number>
  _perPartLoaded?: Record<string, number>
  // UI display control
  isHidden?: boolean
  isCancelling?: boolean // Track if file is being cancelled (waiting for serverFileId)
  isRetrying?: boolean // [RETRY_FAILED] Indicates folder is retrying failed files
  // Time estimation properties
  uploadStartTime?: number // When upload phase started
  timeEstimate?: string // Formatted time estimate (e.g., "5m left", "2h left")
  lastEstimateUpdate?: number // Last time estimate was calculated
  averageUploadSpeed?: number // Average upload speed in bytes per second
  // Encryption progress tracking (for individual files)
  encryptionProgress?: number // Encryption progress percentage (0-100)
  encryptionBytesProcessed?: number // Bytes processed during encryption
  encryptionTotalBytes?: number // Total bytes to encrypt
  encryptionThroughput?: number // Encryption throughput in bytes per second
  encryptionEta?: number // Estimated time remaining for encryption in seconds
  lastEncryptionUpdate?: number // Last time encryption progress was updated
}

// Helper function to format folder progress display
const formatFolderProgress = (originalName?: string, serverName?: string): string => {
  // Check if there's a real naming conflict (server added suffix like (1), (2), etc.)
  const hasNamingConflict =
    originalName &&
    serverName &&
    originalName !== serverName &&
    ((serverName.includes('(') && serverName.includes(')')) ||
      serverName.trim() !== originalName.trim())

  // If there's a naming conflict, show both names without file count
  if (hasNamingConflict) {
    return `${originalName} uploaded as ${serverName}`
  }

  // Otherwise just show the original name without file count
  const displayName = originalName || serverName || 'Unknown'

  return displayName
}

// Time estimation utility functions
const formatTimeEstimate = (seconds: number): string => {
  if (seconds < 60) {
    return '<1m left'
  }

  const minutes = Math.round(seconds / 60)

  if (minutes < 60) {
    return `${minutes}m left`
  }

  const hours = Math.round(minutes / 60)

  if (hours < 24) {
    return `${hours}h left`
  }

  return '>24h left'
}

const updateAverageSpeed = (
  currentSpeed: number,
  previousAverage?: number,
  weight: number = 0.4, // How much to weight the new speed vs previous average (increased for faster adaptation)
): number => {
  if (!previousAverage || previousAverage <= 0) {
    return currentSpeed
  }

  // Exponential moving average with adaptive weighting
  // Weight recent speeds more heavily to adapt to changing network conditions
  return previousAverage * (1 - weight) + currentSpeed * weight
}

const pickFile = (r: any) => (r?.file ? r.file : r)

// Enhanced upload function with debugging
const uploadToSignedUrlWithDebug = async (
  signedUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
  abortController?: AbortController,
  onProgressBytes?: (loaded: number, total: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Set up abort handling
    if (abortController) {
      abortController.signal.addEventListener('abort', () => {
        xhr.abort()
        reject(new Error('Upload aborted'))
      })
    }

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100)

        onProgress?.(progress)
        onProgressBytes?.(event.loaded, event.total)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.open('PUT', signedUrl)
    xhr.setRequestHeader(
      'Content-Type',
      file.type || getMimeTypeFromExtension(file.name || 'unknown'),
    )
    xhr.send(file)
  })
}

// Modern axios-based chunk upload to signed URL with progress tracking
const uploadChunkToSignedUrl = async (
  signedUrl: string,
  chunk: Blob,
  onProgress?: (percent: number) => void,
  abortController?: AbortController,
  onProgressBytes?: (loaded: number, total: number) => void,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Check if already aborted before starting
    if (abortController?.signal.aborted) {
      reject(new Error('Upload aborted'))

      return
    }
    const xhr = new XMLHttpRequest()

    // Set up abort handling
    if (abortController) {
      abortController.signal.addEventListener('abort', () => {
        xhr.abort()
        reject(new Error('Upload aborted'))
      })
    }

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100)

        onProgress?.(progress)
        onProgressBytes?.(event.loaded, event.total)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Extract ETag from response headers
        const etag = xhr.getResponseHeader('ETag')

        if (etag) {
          resolve(etag.replace(/"/g, '')) // Remove quotes from ETag
        } else {
          resolve('') // Fallback if no ETag
        }
      } else {
        reject(new Error(`Chunk upload failed with status: ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during chunk upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Chunk upload aborted'))
    })

    xhr.open('PUT', signedUrl)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.send(chunk)
  })
}

// Enhanced completion function with retry logic and authentication refresh
const completeUploadWithRetry = async (
  request: CompleteUploadRequest,
  maxRetries: number = 3,
  baseDelay: number = 1000,
): Promise<any> => {
  let lastError: Error | null = null

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Attempting to complete upload (attempt ${attempt}/${maxRetries})`)

      // Add a small delay before completion to ensure S3 has processed the upload
      if (attempt > 1) {
        const delay = baseDelay * Math.pow(2, attempt - 2) // Exponential backoff

        console.log(`Waiting ${delay}ms before retry...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
      const response = await completeUpload(request)

      return response
    } catch (error: any) {
      lastError = error as Error
      console.warn(`Upload completion attempt ${attempt} failed:`, error)

      // If it's a 403 error, it might be due to token expiration
      if (error.response?.status === 403) {
        console.log('403 error detected - this might be due to token expiration')

        // For 403 errors, we might want to try to refresh the token
        // The axios interceptor should handle this automatically, but we can add extra logic here
        if (attempt < maxRetries) {
          console.log('Will retry with potential token refresh...')
          continue
        }
      }

      // For other errors, only retry if it's not the last attempt
      if (attempt < maxRetries) {
        continue
      }
    }
  }

  // If we get here, all retries failed
  throw new Error(
    `Upload completion failed after ${maxRetries} attempts. Last error: ${lastError?.message}`,
  )
}

// React Query hook for uploading files to signed URLs
export const useUploadToSignedUrl = () => {
  return useMutation({
    mutationFn: async ({
      signedUrl,
      file,
      onProgress,
    }: {
      signedUrl: string
      file: File
      onProgress?: (progress: number) => void
    }) => {
      return await uploadToSignedUrlWithDebug(signedUrl, file, onProgress)
    },
    onError: error => {
      console.error('Upload to signed URL failed:', error)
    },
  })
}

// React Query hook for uploading chunks to signed URLs
export const useUploadChunkToSignedUrl = () => {
  return useMutation({
    mutationFn: async ({
      signedUrl,
      chunk,
      onProgress,
    }: {
      signedUrl: string
      chunk: Blob
      onProgress?: (progress: number) => void
    }) => {
      return await uploadChunkToSignedUrl(signedUrl, chunk, onProgress)
    },
    onError: error => {
      console.error('Chunk upload to signed URL failed:', error)
    },
  })
}

// API functions - using the built-in axios instance
const startUpload = async (request: StartUploadRequest): Promise<StartUploadResponse> => {
  return await startSingleUpload(request)
}

const completeUpload = async (request: CompleteUploadRequest): Promise<any> => {
  return await completeSingleUpload(request)
}

const getMimeTypeFromExtension = (filename: string): string => {
  // Add null/undefined check for filename
  if (!filename || typeof filename !== 'string') {
    return 'application/octet-stream'
  }

  const extension = filename.split('.').pop()?.toLowerCase()

  const mimeTypes =
    extension && EXTENSION_TO_MIME[extension] ? EXTENSION_TO_MIME[extension] : 'unknown'

  return mimeTypes
}

// Helper function to format file size in human-readable format
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Helper function to determine upload strategy
const getUploadStrategy = (files: File[]): 'single' | 'multipart' => {
  // Use multipart if any file is larger than threshold OR if there are many files
  const hasLargeFile = files.some(file => file.size > MULTIPART_THRESHOLD)
  const hasManyFiles = files.length > MAX_SINGLE_FILES

  return hasLargeFile || hasManyFiles ? 'multipart' : 'single'
}

// Helper function to split file into chunks with ultra-optimized sizing
const splitFileIntoChunks = (file: File): Blob[] => {
  const chunks: Blob[] = []
  let offset = 0

  // Ultra-optimized chunk sizing for maximum performance
  const adaptiveChunkSize = CHUNK_SIZE

  const numChunks = Math.ceil(file.size / adaptiveChunkSize)

  console.log(
    `🚀 Ultra-optimized: ${(file.size / 1024 / 1024).toFixed(2)}MB file → ${numChunks} chunks of ${(adaptiveChunkSize / 1024 / 1024).toFixed(2)}MB each`,
  )

  while (offset < file.size) {
    const chunk = file.slice(offset, offset + adaptiveChunkSize)

    chunks.push(chunk)
    offset += adaptiveChunkSize
  }

  return chunks
}

// Utility function for debugging upload strategy selection
export const debugUploadStrategy = (files: File[]) => {
  const strategy = getUploadStrategy(files)
  const largeFiles = files.filter(f => f.size > MULTIPART_THRESHOLD)
  const totalSize = files.reduce((sum, f) => sum + f.size, 0)

  console.log('Upload Strategy Debug:', {
    strategy,
    fileCount: files.length,
    totalSize: `${(totalSize / 1024 / 1024).toFixed(2)} MB`,
    largeFiles: largeFiles.map(f => ({
      name: f.name,
      size: `${(f.size / 1024 / 1024).toFixed(2)} MB`,
    })),
    threshold: `${(MULTIPART_THRESHOLD / 1024 / 1024).toFixed(2)} MB`,
    chunkSize: `${(CHUNK_SIZE / 1024 / 1024).toFixed(2)} MB`,
    maxConcurrent: MAX_CONCURRENT_CHUNKS,
  })

  return strategy
}

// Utility function for validating files before upload (exported for testing)
export const validateFilesForUpload = (files: File[]) => {
  const validationErrors: string[] = []
  const validFiles: File[] = []

  // Check individual file size limits
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      validationErrors.push(
        `File "${(file.name || 'unknown').slice(0, 40)}..." (${formatFileSize(file.size)}) exceeds the maximum size limit of 1 TB`,
      )
    } else {
      validFiles.push(file)
    }
  }

  // Check total file count limit
  if (validFiles.length > MAX_FILE_COUNT) {
    validationErrors.push(
      `Cannot upload more than ${MAX_FILE_COUNT} files at once. You selected ${validFiles.length} files.`,
    )
    // Only keep the first MAX_FILE_COUNT files
    validFiles.splice(MAX_FILE_COUNT)
  }

  return {
    validFiles,
    validationErrors,
    rejectedCount: files.length - validFiles.length,
    isValid: validationErrors.length === 0 && validFiles.length > 0,
  }
}

// Performance optimization utility
export const optimizeUploadSettings = (fileSize: number, connectionSpeed?: number) => {
  const sizeMB = fileSize / 1024 / 1024

  // Adaptive settings based on file size and connection
  let optimalChunkSize = CHUNK_SIZE
  let optimalConcurrency = MAX_CONCURRENT_CHUNKS

  if (sizeMB > 1000) {
    // Very large files (>1GB)
    optimalChunkSize = 50 * 1024 * 1024 // 50MB
    optimalConcurrency = 2 // Reduce concurrency for very large files
  } else if (sizeMB > 500) {
    // Large files (500MB-1GB)
    optimalChunkSize = 30 * 1024 * 1024 // 30MB
    optimalConcurrency = 3
  } else if (sizeMB > 100) {
    // Medium files (100MB-500MB)
    optimalChunkSize = 20 * 1024 * 1024 // 20MB
    optimalConcurrency = 3
  } else {
    // Small files (<100MB)
    optimalChunkSize = 10 * 1024 * 1024 // 10MB
    optimalConcurrency = 4
  }

  // Adjust based on connection speed if available
  if (connectionSpeed) {
    const speedMBps = connectionSpeed / 1024 / 1024

    if (speedMBps < 10) {
      // Slow connection - reduce chunk size and concurrency
      optimalChunkSize = Math.min(optimalChunkSize, 5 * 1024 * 1024)
      optimalConcurrency = Math.min(optimalConcurrency, 2)
    } else if (speedMBps > 50) {
      // Fast connection - increase chunk size and concurrency
      optimalChunkSize = Math.min(optimalChunkSize * 1.5, 100 * 1024 * 1024)
      optimalConcurrency = Math.min(optimalConcurrency + 1, 5)
    }
  }

  return {
    chunkSize: optimalChunkSize,
    concurrency: optimalConcurrency,
    estimatedTime:
      ((fileSize / optimalChunkSize) * (optimalChunkSize / (1024 * 1024 * 10))) /
      optimalConcurrency, // rough estimate
  }
}

// Connection speed detection utility
export const detectConnectionSpeed = async (): Promise<number> => {
  return new Promise(resolve => {
    const testFileSize = 1 * 1024 * 1024 // 1MB test file
    const testUrl = 'https://httpbin.org/bytes/1048576' // 1MB test endpoint

    const startTime = Date.now()

    fetch(testUrl)
      .then(response => response.blob())
      .then(() => {
        const endTime = Date.now()
        const duration = (endTime - startTime) / 1000 // seconds
        const speed = testFileSize / duration // bytes per second

        console.log(`🌐 Connection speed detected: ${(speed / 1024 / 1024).toFixed(2)} MB/s`)
        resolve(speed)
      })
      .catch(() => {
        // Fallback to default speed if test fails
        console.log('🌐 Connection speed test failed, using default settings')
        resolve(10 * 1024 * 1024) // Assume 10 MB/s
      })
  })
}

/**
 * Sanitize file name for Sentry (remove sensitive paths)
 */
const sanitizeFileName = (fileName: string): string => {
  // Remove full paths, keep only filename
  const parts = fileName.split(/[/\\]/)
  const name = parts[parts.length - 1] || fileName

  // Remove extension if it might contain sensitive info
  const lastDot = name.lastIndexOf('.')

  if (lastDot > 0) {
    return name.substring(0, lastDot) + '[ext]'
  }

  return name
}

// Single file upload handler
const handleSingleFileUpload = async (
  file: File,
  uploadingFile: EnhancedUploadingFile,
  updateProgress: (progress: number) => void,
  onComplete: (fileData: any) => void,
  onError: (error: Error) => void,
  parentId?: string,
  parentPath?: string,
  abortController?: AbortController,
  setState?: (updater: (state: GlobalFileUploadState) => GlobalFileUploadState) => void,
  queryClient?: any,
) => {
  const sanitizedName = sanitizeFileName(file.name)

  return Sentry.startSpan(
    {
      op: 'file.upload',
      name: `Upload ${sanitizedName}`,
    },
    async span => {
      span.setAttribute('file.name', sanitizedName)
      span.setAttribute('file.size', file.size)
      span.setAttribute('file.type', file.type || 'unknown')
      span.setAttribute('file.upload_method', 'single')

      // Declare startResponse outside try block so it's accessible in catch
      let startResponse: any = null

      try {
        // Step 1: Start upload
        const startRequest: StartSingleUploadUrlDtoWithPath = {
          name: file.name || 'unknown',
          mimeType: file.type || getMimeTypeFromExtension(file.name || 'unknown'),
          mediaType: getMediaTypeByExtension(file.name),
          sizeBytes: file.size,
          parentId: parentId || undefined,
          parentPath: parentPath || undefined,
        }

        // Check if aborted before starting
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        // Start upload - don't cancel this request, we need serverFileId for proper cleanup
        startResponse = await startUpload(startRequest)

        // Store the server file ID and update the file name with the API response
        if (setState) {
          setState((state: GlobalFileUploadState) => {
            const updatedState = {
              ...state,
              uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                f.id === uploadingFile.id
                  ? {
                      ...f,
                      serverFileId: startResponse.file.id,
                      name: startResponse.file.name || f.name, // Update name with API response
                      originalName: f.originalName || f.name, // Preserve original name if not already set
                      fileStatus: startResponse.file.status, // Update file status from backend
                      mimeType: startResponse.file.mimeType,
                    }
                  : f,
              ),
            }

            // Save to localStorage after serverFileId is assigned
            saveUploadingFilesToStorage(updatedState.uploadingFiles)

            return updatedState
          })
        }
        // Optimistically add ghost file to cache for instant display, then invalidate
        // This provides immediate UI feedback while backend file is being fetched
        if (queryClient && !shouldSkipCacheUpdatesWithLog('both', 'single file ghost upload')) {
          // Create ghost file entry from backend response    console.log('uplaod in trash etcet et etet')

          const ghostFile = {
            id: startResponse.file.id,
            name: startResponse.file.name,
            path: startResponse.file.path || '',
            parentId: parentId || '',
            isDir: false,
            status: startResponse.file.status, // Will be 'UPLOADING'
            sizeBytes: file.size,
            isFavorite: false,
            mimeType: startResponse.file.mimeType,
            mediaType: startResponse.file.mediaType,
            createdAt: startResponse.file.createdAt,
            updatedAt: startResponse.file.updatedAt,
            trashedAt: null,
            effectivelyTrashedBy: null,
          }

          // Add ghost file to cache in alphabetically sorted position
          const { addItemToSortedCache } = await import('@qse/utils')
          const addGhostFileToData = (oldData: any) => addItemToSortedCache(oldData, ghostFile)

          // Get ALL file list queries and update only the ones that match the file's parentId
          const allFileQueries = queryClient.getQueriesData({
            queryKey: queryKeys.files.lists(), // ['files', 'list']
          })

          allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
            if (!cacheData) return

            // Extract filter from query key: ['files', 'list', { parentId: 'xyz', ... }]
            const queryFilter = queryKey[2] || {}
            const queryParentId = queryFilter.parentId

            // Check if this query should contain the file (matching parentId)
            const shouldContainFile =
              queryParentId === parentId ||
              (queryParentId === null &&
                (parentId === '' || parentId === null || parentId === undefined)) ||
              (queryParentId === '' && (parentId === null || parentId === undefined)) ||
              (queryParentId === undefined &&
                (parentId === null || parentId === '' || parentId === undefined))

            if (shouldContainFile) {
              queryClient.setQueryData(queryKey, (oldData: any) => addGhostFileToData(oldData))
            }
          })

          // Don't invalidate immediately - let the ghost file stay in cache to prevent UI flash
          // The file will be updated when the upload completes or when the user navigates away
        }

        console.log('uplaod in trash etcet et etet')

        // Check if aborted before proceeding
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        // Step 2: Upload to signed URL with progress tracking and retry logic
        const { retryS3Upload, retryFolderUploadApi } = await import('@qse/utils')

        await retryS3Upload(
          () =>
            uploadToSignedUrlWithDebug(
              startResponse.signedUrl,
              file,
              updateProgress,
              abortController,
            ),
          {
            fileName: file.name,
          },
        )
        // Check if aborted before completing
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        await new Promise(resolve => setTimeout(resolve, 2000)) // 2 second delay
        // Check if aborted before completing
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }
        // Step 4: Complete upload
        const completeResponse = await retryFolderUploadApi(
          () => completeUploadWithRetry({ fileId: startResponse.file.id }),
          {
            operation: 'completeSingleUpload',
            fileName: file.name,
          },
        )
        const completedFile = pickFile(completeResponse)

        // Remove from localStorage on successful completion
        if (completedFile) {
          removeUploadFromStorage(completedFile.id, 'file')
        }

        span.setStatus({ code: 1 }) // 1 = OK
        onComplete(completedFile)
      } catch (error) {
        // Remove from localStorage on error
        // Use startResponse.file.id if available (upload started), otherwise try uploadingFile.serverFileId
        const fileIdToRemove = startResponse?.file?.id || uploadingFile.serverFileId

        if (fileIdToRemove) {
          removeUploadFromStorage(fileIdToRemove, 'file')
        }

        span.setStatus({ code: 2 }) // 2 = ERROR
        span.setAttribute('error', true)
        onError(error as Error)
      }
    },
  )
}

// Multipart file upload handler with performance monitoring
const handleMultipartFileUpload = async (
  file: File,
  uploadingFile: EnhancedUploadingFile,
  updateProgress: (progress: number) => void,
  updateChunkInfo: (currentChunk: number, totalChunks: number) => void,
  onComplete: (fileData: any) => void,
  onError: (error: Error) => void,
  parentId?: string,
  parentPath?: string,
  abortController?: AbortController,
  setState?: (updater: (state: GlobalFileUploadState) => GlobalFileUploadState) => void,
  queryClient?: any,
) => {
  const sanitizedName = sanitizeFileName(file.name)

  return Sentry.startSpan(
    {
      op: 'file.upload',
      name: `Upload ${sanitizedName} (multipart)`,
    },
    async span => {
      span.setAttribute('file.name', sanitizedName)
      span.setAttribute('file.size', file.size)
      span.setAttribute('file.type', file.type || 'unknown')
      span.setAttribute('file.upload_method', 'multipart')

      // Declare startResponse outside try block so it's accessible in catch
      let startResponse: any = null

      try {
        // Step 1: Start multipart upload
        const startRequest: StartMultipartUploadUrlDtoWithPath = {
          name: file.name || 'unknown',
          mimeType: file.type || getMimeTypeFromExtension(file.name || 'unknown'),
          mediaType: getMediaTypeByExtension(file.name),
          sizeBytes: file.size,
          parentId: parentId || undefined,
          parentPath: parentPath || undefined,
        }

        // Check if aborted before starting
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        // Start multipart upload - don't cancel this request, we need serverFileId for proper cleanup
        startResponse = await startMultipartUpload(startRequest)

        // Import retry utilities
        const { retryFolderUploadApi } = await import('@qse/utils')

        // Store the server file ID and update the file name with the API response
        if (setState) {
          setState((state: GlobalFileUploadState) => {
            const updatedState = {
              ...state,
              uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                f.id === uploadingFile.id
                  ? {
                      ...f,
                      serverFileId: startResponse.file.id,
                      name: startResponse.file.name || f.name, // Update name with API response
                      originalName: f.originalName || f.name, // Preserve original name if not already set
                      fileStatus: startResponse.file.status, // Update file status from backend
                    }
                  : f,
              ),
            }

            // Save to localStorage after serverFileId is assigned
            saveUploadingFilesToStorage(updatedState.uploadingFiles)

            return updatedState
          })
        }

        // Optimistically add ghost file to cache for instant display, then invalidate
        if (queryClient && !shouldSkipCacheUpdatesWithLog('both', 'multipart file ghost upload')) {
          const ghostFile = {
            id: startResponse.file.id,
            name: startResponse.file.name,
            path: startResponse.file.path || '',
            parentId: parentId || '',
            isDir: false,
            status: startResponse.file.status,
            sizeBytes: file.size,
            isFavorite: false,
            mimeType: startResponse.file.mimeType,
            mediaType: startResponse.file.mediaType,
            createdAt: startResponse.file.createdAt,
            updatedAt: startResponse.file.updatedAt,
            trashedAt: null,
            effectivelyTrashedBy: null,
          }

          // Add ghost file to cache in alphabetically sorted position
          const { addItemToSortedCache } = await import('@qse/utils')
          const addGhostFileToData = (oldData: any) => addItemToSortedCache(oldData, ghostFile)

          // Get ALL file list queries and update only the ones that match the file's parentId
          const allFileQueries = queryClient.getQueriesData({
            queryKey: queryKeys.files.lists(), // ['files', 'list']
          })

          allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
            if (!cacheData) return

            // Extract filter from query key: ['files', 'list', { parentId: 'xyz', ... }]
            const queryFilter = queryKey[2] || {}
            const queryParentId = queryFilter.parentId

            // Check if this query should contain the file (matching parentId)
            const shouldContainFile =
              queryParentId === parentId ||
              (queryParentId === null &&
                (parentId === '' || parentId === null || parentId === undefined)) ||
              (queryParentId === '' && (parentId === null || parentId === undefined)) ||
              (queryParentId === undefined &&
                (parentId === null || parentId === '' || parentId === undefined))

            if (shouldContainFile) {
              queryClient.setQueryData(queryKey, (oldData: any) => addGhostFileToData(oldData))
            }
          })

          // Don't invalidate immediately - let the ghost file stay in cache to prevent UI flash
          // The files will be updated when the upload completes or when the user navigates away
        }

        // Check if aborted before proceeding
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        // Step 2: Split file into chunks
        const chunks = splitFileIntoChunks(file)

        // Step 3: Get signed URLs for chunks
        const getUrlsRequest: GetMultipartUrlsRequest = {
          fileId: startResponse.file.id,
          partsNumber: chunks.length,
        }

        const urlsResponse = await retryFolderUploadApi(
          () => getMultipartFileSignedUrls(getUrlsRequest),
          {
            operation: 'getMultipartFileSignedUrls',
            fileName: file.name,
          },
        )

        // Check if aborted before proceeding
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        // Step 4: Upload chunks with ultra-optimized concurrent control
        const uploadChunksConcurrently = async (chunks: Blob[], urls: any[]) => {
          const etags: string[] = []
          let totalUploaded = 0
          const totalSize = chunks.reduce((sum, chunk) => sum + chunk.size, 0)

          const updateOverallProgress = (chunkProgress: number, chunkSize: number) => {
            const chunkContribution = (chunkProgress / 100) * chunkSize
            const overallProgress = Math.round(
              ((totalUploaded + chunkContribution) / totalSize) * 100,
            )

            updateProgress(overallProgress)
          }

          const uploadChunkWithRetry = async (chunk: Blob, index: number) => {
            const { retryS3Upload } = await import('@qse/utils')

            const chunkProgress = (progress: number) => {
              updateOverallProgress(progress, chunk.size)
            }

            const etag = await retryS3Upload(
              () => uploadChunkToSignedUrl(urls[index].url, chunk, chunkProgress, abortController),
              {
                fileName: file.name,
                chunkIndex: index,
                totalChunks: chunks.length,
              },
            )

            etags[index] = etag
            totalUploaded += chunk.size
            updateChunkInfo(index + 1, chunks.length)

            return etag
          }

          const processChunk = async (index: number) => {
            // Check if aborted before processing each chunk
            if (abortController?.signal.aborted) {
              throw new Error('Upload aborted')
            }

            return uploadChunkWithRetry(chunks[index], index)
          }

          // Process chunks sequentially for better control
          for (let i = 0; i < chunks.length; i++) {
            await processChunk(i)
          }

          return etags
        }

        const etags = await uploadChunksConcurrently(chunks, urlsResponse.parts)

        // Check if aborted before completing
        if (abortController?.signal.aborted) {
          throw new Error('Upload aborted')
        }

        // Step 5: Complete multipart upload
        const completeRequest: CompleteMultipartRequest = {
          fileId: startResponse.file.id,
          parts: etags.map((etag, index) => ({
            ETag: etag,
            PartNumber: index + 1,
          })),
        }
        const completeResponse = await retryFolderUploadApi(
          () => completeMultipartUpload(completeRequest),
          {
            operation: 'completeMultipartUpload',
            fileName: file.name,
          },
        )
        const completedFile = pickFile(completeResponse)

        // Remove from localStorage on successful completion
        if (completedFile) {
          removeUploadFromStorage(completedFile.id, 'file')
        }

        span.setStatus({ code: 1 }) // 1 = OK
        onComplete(completedFile)
      } catch (error) {
        // Remove from localStorage on error
        // Use startResponse.file.id if available (upload started), otherwise try uploadingFile.serverFileId
        const fileIdToRemove = startResponse?.file?.id || uploadingFile.serverFileId

        if (fileIdToRemove) {
          removeUploadFromStorage(fileIdToRemove, 'file')
        }

        span.setStatus({ code: 2 }) // 2 = ERROR
        span.setAttribute('error', true)
        onError(error as Error)
      }
    },
  )
}

interface GlobalFileUploadState {
  isUploadModalOpen: boolean
  uploadingFiles: EnhancedUploadingFile[]
  driveFiles: FileItem[]
  abortControllers: Map<number, AbortController>
  folderAbortControllers: Map<string, AbortController> // folderUploadId -> parent AbortController
  isAborted: boolean // Flag to track if uploads have been aborted
  beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null
  batchSize?: number // Configurable batch size
  batchDelay?: number // Configurable delay between batches
  uploadQueue: Array<() => Promise<void>> // Queue for pending uploads
  activeUploads: number // Count of currently active uploads
  deletedFolderIds: Set<string> // Track permanently deleted destination folders during upload
  setIsUploadModalOpen: (isOpen: boolean) => void
  setUploadingFiles: (files: EnhancedUploadingFile[]) => void
  setDriveFiles: (files: FileItem[]) => void
  setBatchSize: (size: number) => void
  setBatchDelay: (delay: number) => void
  addFiles: (
    files: (File & { path?: string })[],
    parentId?: string,
    invalidateQueryKeys?: any[],
    queryClient?: any,
    parentPath?: string,
    folderInit?: FolderInit,
  ) => void
  handleRetry: (fileId: number, invalidateQueryKeys?: any[], queryClient?: any) => void
  // [RETRY_FAILED] Retry all retryable failed files in a folder upload
  handleRetryFailedFiles: (folderUploadId: string, queryClient?: any) => Promise<void>
  handleAbort: (fileId: number, queryClient?: any) => Promise<void>
  handleAbortAll: (queryClient?: any) => Promise<void>
  handleLogoutAbort: (queryClient?: any) => Promise<void>
  setupBeforeUnloadListener: () => void
  removeBeforeUnloadListener: () => void
  updateFileStatusFromWebSocket: (fileId: string, status: FileStatus, queryClient?: any) => void
  updateFileEncryptionProgress: (
    fileId: string,
    progress: {
      percentage: number
      bytesProcessed: number
      totalBytes: number
      throughput: number
      estimatedTimeRemaining: number
    },
  ) => void
  updateFileEncryptionFailureForFolder: (
    fileId: string,
    fileName: string,
    filePath: string,
    parentId?: string,
  ) => void
  updateFolderUploadProgressFromWebSocket: (
    payload: FolderUploadProgressPayload,
    queryClient?: any,
  ) => void
  updateFolderUploadProgress: (payload: any) => void
  clearCompleted: () => void
  reset: () => void
  processUploadQueue: () => void
  addDeletedFolderId: (folderId: string) => void
  addDeletedFolderIds: (folderIds: string[]) => void
  clearDeletedFolderIds: () => void
  checkAndAbortAffectedFiles: (deletedFolderIds: string[]) => Promise<void>
  recalculateFolderProgress: (folderUploadId: string) => void
  getCurrentlyUploadingFolderIds: () => string[]
  checkAndCancelStaleUploads: (queryClient?: any) => Promise<void>
  registerPendingFolderUpload: (payload: {
    clientRequestId: string
    name: string
    totalFiles: number
    totalBytes: number
    parentPath: string
  }) => void
  markFolderInitiateFailed: (payload: { clientRequestId: string; errorMessage: string }) => void
}

export const useGlobalFileUpload = create<GlobalFileUploadState>((set, get) => {
  // Setup BroadcastChannel listener immediately when store is created
  // This ensures we can respond to other tabs' checks even if we haven't called checkAndCancelStaleUploads yet
  // This prevents race conditions where a new tab opens and checks for stale uploads before the uploading tab has set up its listener
  setupBroadcastChannelListener(get, set)

  return {
    isUploadModalOpen: false,
    uploadingFiles: [],
    driveFiles: [],
    abortControllers: new Map<number, AbortController>(),
    folderAbortControllers: new Map<string, AbortController>(),
    isAborted: false, // Initialize the new state
    deletedFolderIds: new Set<string>(), // Initialize deleted folder IDs tracking
    beforeUnloadHandler: null,
    batchSize: BATCH_SIZE, // Default batch size
    batchDelay: BATCH_DELAY, // Default batch delay
    uploadQueue: [], // Initialize upload queue
    activeUploads: 0, // Initialize active uploads counter
    setIsUploadModalOpen: (isOpen: boolean) => set({ isUploadModalOpen: isOpen }),
    setUploadingFiles: (files: UploadingFile[]) => set({ uploadingFiles: files }),
    setDriveFiles: (files: FileItem[]) => set({ driveFiles: files }),
    setBatchSize: (size: number) => set({ batchSize: size }),
    setBatchDelay: (delay: number) => set({ batchDelay: delay }),
    registerPendingFolderUpload: ({ clientRequestId, name, totalFiles, totalBytes, parentPath }) =>
      set((state: GlobalFileUploadState) => {
        const existingPlaceholder = state.uploadingFiles.find(
          file => file.clientRequestId === clientRequestId,
        )

        if (existingPlaceholder) {
          return state
        }

        const placeholderId = Date.now() + Math.round(Math.random() * 1000)

        const placeholderEntry: EnhancedUploadingFile = {
          id: placeholderId,
          name,
          originalName: name,
          serverName: name,
          progress: 0,
          status: FileStatus.UPLOADING,
          type: 'folder',
          path: '',
          parentId: undefined,
          parentPath,
          originalFile: undefined,
          uploadStrategy: 'folder',
          mediaType: 'FOLDER' as MediaType,
          mimeType: 'folder',
          isFolderUpload: true,
          totalFiles,
          uploadedFiles: 0,
          failedFiles: 0,
          failedUploads: 0,
          failedFileDetails: [],
          folderUploadId: undefined,
          totalBytes,
          uploadedBytes: 0,
          _perFileLoaded: {},
          _perPartLoaded: {},
          uploadStartTime: Date.now(),
          timeEstimate: '',
          lastEstimateUpdate: Date.now(),
          averageUploadSpeed: undefined,
          clientRequestId,
        }

        const updatedState: GlobalFileUploadState = {
          ...state,
          uploadingFiles: [placeholderEntry, ...state.uploadingFiles],
          isUploadModalOpen: true,
        }

        saveUploadingFilesToStorage(updatedState.uploadingFiles)

        return updatedState
      }),
    markFolderInitiateFailed: ({ clientRequestId, errorMessage }) =>
      set((state: GlobalFileUploadState) => {
        const hasMatchingPlaceholder = state.uploadingFiles.some(
          file => file.clientRequestId === clientRequestId,
        )

        if (!hasMatchingPlaceholder) {
          return state
        }

        const updatedFiles = state.uploadingFiles.map(file => {
          if (file.clientRequestId !== clientRequestId) {
            return file
          }

          return {
            ...file,
            status: FileStatus.FAILED,
            errorMessage,
            progress: 0,
            folderUploadId: file.folderUploadId,
            timeEstimate: '',
          }
        })

        const updatedState: GlobalFileUploadState = {
          ...state,
          uploadingFiles: updatedFiles,
        }

        saveUploadingFilesToStorage(updatedState.uploadingFiles)

        return updatedState
      }),
    addFiles: (
      files: (File & { path?: string })[],
      parentId?: string,
      invalidateQueryKeys?: any[],
      queryClient?: any,
      parentPath?: string,
      folderInit?: FolderInit,
    ) => {
      // Create upload task and add to queue
      const uploadTask = async () => {
        const isFolderUpload = !!folderInit?.isFolderUpload

        // ───────────────────────────────
        // Helpers (محليّة)
        // ───────────────────────────────
        const normalizePath = (p?: string) => {
          if (!p) return '/'
          let s = p.replace(/\\/g, '/').replace(/\/+/g, '/').trim()

          if (!s.startsWith('/')) s = '/' + s
          if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1)

          return s
        }
        const pathKey = (p?: string) => {
          if (!p) return '/'
          let s = p.replace(/\\/g, '/').replace(/\/+/g, '/').trim()

          s = s
            .normalize('NFKC')
            .replace(/\u00A0|\u202F/g, ' ')
            .replace(/ +/g, ' ')
          s = s.replace(/[^\p{Letter}\p{Number} ./_\-/]+/gu, '').toLowerCase()
          if (!s.startsWith('/')) s = '/' + s
          if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1)

          return s
        }

        const getClientParentDir = (
          entry: any,
          rootNameFallback: string,
          serverFolderName: string,
        ): string => {
          const raw = entry.path || entry.webkitRelativePath || entry.name || ''
          const clean = raw.replace(/\\/g, '/')
          const parts = clean.split('/').filter(Boolean)

          // For files in the root folder, parentParts should be [serverFolderName]
          // For files in subfolders, parentParts should be the full path to the parent folder
          let parentParts: string[]

          if (parts.length === 1) {
            // File is directly in root folder - use server name
            parentParts = [serverFolderName]
          } else if (parts.length === 2 && parts[0] === rootNameFallback) {
            // File is directly in root folder (path like "rootName/fileName") - use server name
            parentParts = [serverFolderName]
          } else {
            // File is in a subfolder - replace the root folder name with server name
            const pathParts = parts.slice(0, -1)

            if (pathParts[0] === rootNameFallback) {
              pathParts[0] = serverFolderName
            }
            parentParts = pathParts
          }

          const p = normalizePath(parentParts.join('/'))
          const result = p === '/' || p === '' ? `/${serverFolderName}` : p

          return result
        }
        const uploadTypeFor = (sizeBytes: number) =>
          sizeBytes >= MULTIPART_THRESHOLD ? ('MULTIPART' as const) : ('SINGLE' as const)

        const isFileLike = (x: any) =>
          x &&
          (x instanceof File ||
            (typeof x.size === 'number' &&
              typeof x.type === 'string' &&
              typeof x.arrayBuffer === 'function'))

        // Filter out placeholder files and hidden files (files starting with .)
        // Hidden files are not visible to users in their file explorer, so we shouldn't upload them
        const sanitizedFiles = files.filter(f => {
          const isKeepFile = f.name === '.qse.keep' || f.type === 'application/x-qse-dir'

          return !isKeepFile && !shouldFilterFile(f.name)
        })

        // ───────────────────────────────
        // مسار: رفع فولدر (folderInit موجود)
        // ───────────────────────────────
        if (isFolderUpload && folderInit) {
          const {
            rootFolderId,
            rootOriginalName,
            serverName,
            rootServerPath,
            pathToId,
            totalFiles,
            totalBytes,
          } = folderInit

          // IMPORTANT: Use the server-assigned folder name to ensure path mapping works correctly
          // when the server renames folders due to conflicts (e.g., "Folder" -> "Folder (1)")
          const serverFolderName = serverName // Server-assigned name from initiateFolderUpload

          try {
            // ادخال Progress واحد للفولدر
            const displayName =
              serverName !== rootOriginalName && /\(\d+\)/.test(serverName)
                ? `${rootOriginalName} uploaded as ${serverName}`
                : rootOriginalName

            // Don't calculate initial time estimate here - wait for real upload data
            // This prevents showing an inaccurate estimate that jumps when real data arrives
            // The estimate will be calculated once we have actual progress data
            const initialTimeEstimate = ''

            const clientRequestId = folderInit.clientRequestId
            const existingPlaceholder = clientRequestId
              ? get().uploadingFiles.find(file => file.clientRequestId === clientRequestId)
              : undefined
            const folderProgressEntryId = existingPlaceholder?.id ?? Date.now() + Math.random()

            const folderProgressEntry: EnhancedUploadingFile = {
              id: folderProgressEntryId,
              name: displayName,
              originalName: rootOriginalName,
              serverName,
              progress: totalFiles === 0 ? 100 : 0,
              status: totalFiles === 0 ? FileStatus.AVAILABLE : FileStatus.UPLOADING,
              type: 'folder',
              path: '',
              parentId,
              parentPath,
              originalFile: undefined,
              uploadStrategy: 'folder',
              mediaType: 'FOLDER' as MediaType,
              mimeType: 'folder',
              isFolderUpload: true,
              totalFiles,
              uploadedFiles: 0,
              failedFiles: 0,
              failedUploads: existingPlaceholder?.failedUploads ?? 0,
              failedFileDetails: existingPlaceholder?.failedFileDetails ?? [],
              folderUploadId: rootFolderId,
              totalBytes,
              uploadedBytes: 0,
              _perFileLoaded: {},
              _perPartLoaded: {},
              uploadStartTime: Date.now(),
              timeEstimate: initialTimeEstimate,
              lastEstimateUpdate: Date.now(),
              averageUploadSpeed: undefined,
              pathToId: pathToId, // Store the folder structure mapping
              clientRequestId,
            }

            // Create parent abort controller for the entire folder upload
            const folderAbortController = new AbortController()

            set((s: GlobalFileUploadState) => {
              const existingIndex = clientRequestId
                ? s.uploadingFiles.findIndex(file => file.clientRequestId === clientRequestId)
                : -1
              const mergedEntry =
                existingIndex >= 0
                  ? {
                      ...s.uploadingFiles[existingIndex],
                      ...folderProgressEntry,
                      id: s.uploadingFiles[existingIndex].id,
                      failedUploads:
                        s.uploadingFiles[existingIndex].failedUploads ??
                        folderProgressEntry.failedUploads,
                      failedFileDetails:
                        s.uploadingFiles[existingIndex].failedFileDetails ??
                        folderProgressEntry.failedFileDetails,
                      uploadedFiles:
                        totalFiles === 0
                          ? totalFiles
                          : s.uploadingFiles[existingIndex].uploadedFiles || 0,
                      folderUploadId: rootFolderId,
                      clientRequestId,
                    }
                  : folderProgressEntry

              const uploadingFiles =
                existingIndex >= 0
                  ? s.uploadingFiles.map((file, index) =>
                      index === existingIndex ? mergedEntry : file,
                    )
                  : [mergedEntry, ...s.uploadingFiles]

              const updatedState = {
                ...s,
                uploadingFiles,
                isUploadModalOpen: true,
                isAborted: false,
                folderAbortControllers: new Map(s.folderAbortControllers).set(
                  rootFolderId,
                  folderAbortController,
                ),
              }

              // Save to localStorage after folderUploadId is assigned
              saveUploadingFilesToStorage(updatedState.uploadingFiles)

              return updatedState
            })

            // Handle empty folder uploads (no files to process)
            if ((totalFiles ?? 0) === 0 || sanitizedFiles.length === 0) {
              // Mark folder upload as instantly completed
              set(state => {
                const updatedControllers = new Map(state.folderAbortControllers)

                updatedControllers.delete(rootFolderId)

                return {
                  uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
                    if (f.id === folderProgressEntry.id) {
                      return {
                        ...f,
                        status: FileStatus.AVAILABLE,
                        progress: 100,
                        uploadedFiles: 0,
                        failedFiles: 0,
                        failedUploads: 0,
                        timeEstimate: '',
                      }
                    }

                    return f
                  }),
                  folderAbortControllers: updatedControllers,
                }
              })

              // Remove persisted upload entry since we're already done
              removeUploadFromStorage(rootFolderId, 'folder')

              // No need to keep beforeunload listener for empty uploads
              get().removeBeforeUnloadListener?.()

              return
            }

            // جهّز الرفع - upload exactly 5 files at a time, then complete-batch
            const CHUNK_SIZE = 5

            const chunkArray = <T>(arr: T[], size = CHUNK_SIZE): T[][] => {
              const out: T[][] = []

              for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))

              return out
            }

            const makeUploadingFileEntry = (
              f: any,
              name: string,
              mimeType: string,
              folderId: string,
              clientParentDir: string,
              uploadStrategy: 'single' | 'multipart',
            ): EnhancedUploadingFile => ({
              id: Date.now() + Math.random(),
              name,
              originalName: name,
              progress: 0,
              status: FileStatus.UPLOADING,
              type: mimeType,
              path: f.path ?? '',
              parentId: folderId,
              parentPath: clientParentDir,
              originalFile: f,
              uploadStrategy,
              mediaType: getMediaTypeByExtension(name),
              isHidden: true,
              isFolderUpload: true,
              folderUploadId: rootFolderId,
            })

            const runWithConcurrency = async <T>(tasks: Array<() => Promise<T>>, limit: number) => {
              const results: T[] = []
              let idx = 0
              const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
                while (idx < tasks.length) {
                  const i = idx++

                  results[i] = await tasks[i]()
                }
              })

              await Promise.all(workers)

              return results
            }

            // Helper function to check if a destination folder or any of its parents is deleted
            const isDestinationDeleted = (folderId: string): boolean => {
              // Check if the folder itself is deleted
              if (get().deletedFolderIds.has(folderId)) {
                return true
              }

              // Check if any parent folder in the hierarchy is deleted
              // We need to check the folder tree structure to find parent relationships
              // For now, we'll check all deleted folder IDs to see if any match parent paths
              const deletedFolderIds = get().deletedFolderIds

              // Get the folder path from pathToId mapping
              for (const [path, id] of pathToId.entries()) {
                if (id === folderId) {
                  // Check if any deleted folder is a parent of this path
                  for (const deletedId of deletedFolderIds) {
                    // Find the path of the deleted folder
                    for (const [deletedPath, deletedFolderId] of pathToId.entries()) {
                      if (deletedFolderId === deletedId) {
                        // Check if deleted path is a parent of current path
                        if (path.startsWith(deletedPath + '/') || path === deletedPath) {
                          console.log('🗑️ [FolderUpload] Parent folder deleted:', {
                            currentFolderId: folderId,
                            currentPath: path,
                            deletedParentId: deletedId,
                            deletedParentPath: deletedPath,
                            timestamp: new Date().toISOString(),
                          })

                          return true
                        }
                      }
                    }
                  }
                  break
                }
              }

              return false
            }

            // Helper function to mark file as failed due to deleted destination
            const markFileAsDeleted = (file: File & { path?: string }, destinationId: string) => {
              const failedFileDetail: FailedFileDetail = {
                name: file.name,
                path: file.path || file.webkitRelativePath || file.name,
                reason: 'deleted_destination',
              }

              console.log('🗑️ [FolderUpload] File destination deleted, marking as failed:', {
                fileName: file.name,
                filePath: failedFileDetail.path,
                destinationId,
                timestamp: new Date().toISOString(),
              })

              // Update folder progress with failed file
              // IMPORTANT: Only increment failedUploads (for counter display), NOT failedFiles
              // failedFiles is used in progress calculation and should only count files that
              // actually attempted upload. Pre-upload failures shouldn't affect progress bar.
              set((state: GlobalFileUploadState) => ({
                uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
                  if (f.isFolderUpload && f.folderUploadId === rootFolderId) {
                    const newFailedUploads = (f.failedUploads || 0) + 1
                    const totalFiles = f.totalFiles || 0
                    const uploadedFiles = f.uploadedFiles || 0

                    // Check if all files have been processed (uploaded or failed)
                    const allFilesProcessed = uploadedFiles + newFailedUploads >= totalFiles

                    // If ALL files failed (100% failure - destination deleted scenario)
                    const allFilesFailed = newFailedUploads >= totalFiles && uploadedFiles === 0

                    let newStatus = f.status

                    if (allFilesFailed) {
                      // Complete failure - mark as FAILED
                      newStatus = FileStatus.FAILED
                      console.log(
                        '🗑️ [FolderUpload] All files failed due to deleted destinations:',
                        {
                          folderName: f.serverName || f.originalName,
                          totalFiles,
                          failedUploads: newFailedUploads,
                          uploadedFiles,
                        },
                      )

                      // Show error toast for complete failure
                      ;(async () => {
                        const { toast } = await import('sonner')
                        const folderName = f.serverName || f.originalName || 'Folder'

                        toast.error(
                          `"${folderName}" upload failed. All ${totalFiles} files failed to upload.`,
                          {
                            description: 'Destination folder was deleted or moved during upload',
                            duration: 10000,
                          },
                        )
                      })()
                    } else if (allFilesProcessed) {
                      // All files processed (uploaded or failed)
                      // If some succeeded, move to ENCRYPTING phase
                      // BUT: Don't override if folder is already marked as FAILED
                      if (uploadedFiles > 0 && f.status !== FileStatus.FAILED) {
                        newStatus = FileStatus.ENCRYPTING
                      }
                    }

                    return {
                      ...f,
                      status: newStatus,
                      failedUploads: newFailedUploads,
                      failedFileDetails: addOrUpdateFailedFileDetail(
                        f.failedFileDetails,
                        failedFileDetail,
                      ),
                    }
                  }

                  return f
                }),
              }))
            }

            // جروب الملفات حسب مسارها الأب في العميل
            const groups = new Map<string, (File & { path?: string })[]>()

            console.log('🗂️ [FolderUpload] Grouping files by parent directory:')
            for (const f of sanitizedFiles) {
              const parentDir =
                getClientParentDir(f, rootOriginalName, serverFolderName) || rootServerPath
              const normKey = pathKey(parentDir)

              console.log('🗂️ [FolderUpload] File grouping:', {
                fileName: f.name,
                rawPath: f.path || f.webkitRelativePath,
                parentDir,
                normKey,
                getClientParentDirResult: getClientParentDir(f, rootOriginalName, serverFolderName),
              })
              const arr = groups.get(normKey) || []

              arr.push(f)
              groups.set(normKey, arr)
            }

            console.log('🗂️ [FolderUpload] File groups created:', {
              totalGroups: groups.size,
              groupDetails: Array.from(groups.entries()).map(([key, files]) => ({
                groupKey: key,
                filesCount: files.length,
                fileNames: files.map(f => f.name),
                filePaths: files.map(f => (f as any).path || (f as any).webkitRelativePath),
              })),
              timestamp: new Date().toISOString(),
            })

            console.log(
              '🗂️ [FolderUpload] File groups:',
              Array.from(groups.entries()).map(([key, files]) => ({
                key,
                filesCount: files.length,
                fileNames: files.map(f => f.name),
                filePaths: files.map(f => (f as any).path || (f as any).webkitRelativePath),
              })),
            )

            console.log(
              '🗂️ [FolderUpload] Available path mappings:',
              Array.from(pathToId.entries()),
            )

            // Log total files being processed
            const totalFilesInGroups = Array.from(groups.values()).reduce(
              (sum, files) => sum + files.length,
              0,
            )

            console.log(
              '🗂️ [FolderUpload] Total files in groups:',
              totalFilesInGroups,
              'vs original files:',
              sanitizedFiles.length,
            )

            get().setupBeforeUnloadListener?.()

            // سنحتاج APIين
            const { startBatchFileUpload, bulkCompleteUploads } = await import('@qse/api')

            // Track deleted file IDs to send bulk abort at the end
            const deletedFileIds: string[] = []

            for (const [clientParentDirKey, groupFiles] of groups) {
              if (get().isAborted) break

              console.log('🗂️ [FolderUpload] Processing group:', {
                groupKey: clientParentDirKey,
                filesCount: groupFiles.length,
                fileNames: groupFiles.map(f => f.name),
                timestamp: new Date().toISOString(),
              })

              // resolve إلى folderId على السيرفر
              console.log('🗂️ [FolderUpload] Resolving folder ID for:', clientParentDirKey)
              console.log(
                '🗂️ [FolderUpload] Available path mappings:',
                Array.from(pathToId.entries()),
              )

              // Use enhanced path matching that handles numbered suffixes
              let folderId = pathToId.get(clientParentDirKey)

              console.log('🗂️ [FolderUpload] Direct lookup result:', folderId)

              if (!folderId) {
                console.log(
                  '🗂️ [FolderUpload] Direct lookup failed, trying enhanced path matching...',
                )

                // Use the enhanced path matching function
                folderId = findMatchingPath(clientParentDirKey, pathToId)

                console.log('🗂️ [FolderUpload] Enhanced path matching result:', folderId)
              }

              if (!folderId) {
                console.warn('🗂️ [FolderUpload] No server ID found for group, skipping files:', {
                  groupKey: clientParentDirKey,
                  filesCount: groupFiles.length,
                  fileNames: groupFiles.map(f => f.name),
                  availablePaths: Array.from(pathToId.keys()),
                  timestamp: new Date().toISOString(),
                })
                continue
              }

              console.log(
                '🗂️ [FolderUpload] Resolved folder ID:',
                folderId,
                'for path:',
                clientParentDirKey,
              )

              // 🗑️ BATCH-LEVEL CHECK: Verify destination folder exists before uploading
              if (isDestinationDeleted(folderId)) {
                console.log(
                  '🗑️ [FolderUpload] Destination folder deleted, marking all files as failed:',
                  {
                    groupKey: clientParentDirKey,
                    folderId,
                    filesCount: groupFiles.length,
                    fileNames: groupFiles.map(f => f.name),
                    timestamp: new Date().toISOString(),
                  },
                )

                // Mark all files in this group as failed with deleted_destination reason
                groupFiles.forEach(file => markFileAsDeleted(file, folderId))

                // Add folder ID to deleted tracking set for future checks
                get().addDeletedFolderId(folderId)

                // Skip to next group - don't upload any files to deleted destination
                continue
              }

              const uploadable = groupFiles.filter(isFileLike)

              console.log('🗂️ [FolderUpload] Filtered uploadable files:', {
                groupKey: clientParentDirKey,
                originalFilesCount: groupFiles.length,
                uploadableFilesCount: uploadable.length,
                uploadableFileNames: uploadable.map(f => f.name),
                filteredOutCount: groupFiles.length - uploadable.length,
                timestamp: new Date().toISOString(),
              })

              if (uploadable.length === 0) {
                console.warn('🗂️ [FolderUpload] No uploadable files in group, skipping:', {
                  groupKey: clientParentDirKey,
                  originalFilesCount: groupFiles.length,
                  timestamp: new Date().toISOString(),
                })
                continue
              }

              const chunks = chunkArray(uploadable, CHUNK_SIZE)

              console.log('🗂️ [FolderUpload] Created chunks for upload:', {
                groupKey: clientParentDirKey,
                totalFiles: uploadable.length,
                chunkSize: CHUNK_SIZE,
                totalChunks: chunks.length,
                chunksDetails: chunks.map((chunk, index) => ({
                  chunkIndex: index,
                  filesCount: chunk.length,
                  fileNames: chunk.map(f => f.name),
                })),
                timestamp: new Date().toISOString(),
              })

              // Process each chunk completely: get URLs, upload, complete-batch
              for (let ci = 0; ci < chunks.length; ci++) {
                if (get().isAborted) break

                const chunk = chunks[ci]

                console.log('🗂️ [FolderUpload] Processing chunk:', {
                  groupKey: clientParentDirKey,
                  chunkIndex: ci,
                  totalChunks: chunks.length,
                  filesInChunk: chunk.length,
                  fileNames: chunk.map(f => f.name),
                  timestamp: new Date().toISOString(),
                })

                // سيتم تجميع بيانات الإكمال لهذا الـ chunk فقط
                const chunkPendingUploads: Array<{
                  actualFile: File
                  uploadingFile: EnhancedUploadingFile
                  serverHints?: any
                }> = []
                const chunkUploadCompletions: Array<{
                  id: string
                  uploadType: 'SINGLE' | 'MULTIPART'
                  sizeBytes?: number
                  parts?: Array<{ ETag: string; PartNumber: number }>
                }> = []
                // Track completed file IDs to prevent duplicates
                const completedFileIds = new Set<string>()

                // Silently detect zero-byte files for logging purposes (no warning shown)
                const zeroByteFiles = chunk.filter((f: File) => f.size === 0)

                if (zeroByteFiles.length > 0) {
                  console.log('🗂️ [FolderUpload] Detected zero-byte files:', {
                    count: zeroByteFiles.length,
                    files: zeroByteFiles.map(f => f.name),
                    timestamp: new Date().toISOString(),
                  })
                }

                // Continue with ALL files (including zero-byte files)
                const payload = {
                  parentId: folderId,
                  files: chunk.map((f: any) => {
                    const name =
                      f.name ||
                      String(f.path || '')
                        .split('/')
                        .pop() ||
                      'unnamed'
                    const sizeBytes =
                      typeof f.size === 'number' ? f.size : (f.sizeBytes as number) || 0
                    const mimeType = f.type || getMimeTypeFromExtension(name)
                    const mediaType = getMediaTypeByExtension(name)
                    const uploadType = uploadTypeFor(sizeBytes)
                    const base: any = {
                      id: uuidv4(),
                      name,
                      sizeBytes,
                      mimeType,
                      mediaType,
                      uploadType,
                    }

                    if (uploadType === 'MULTIPART') {
                      const PART = 5 * 1024 * 1024

                      base.partsCount = Math.max(1, Math.ceil(sizeBytes / PART))
                    }

                    return base
                  }),
                }

                if (get().isAborted) return

                // Import retry utilities
                const { retryFolderUploadApi } = await import('@qse/utils')

                let startResp: any

                try {
                  startResp = await retryFolderUploadApi(
                    () => startBatchFileUpload(rootFolderId, payload),
                    {
                      operation: 'startBatchFileUpload',
                      folderId: rootFolderId,
                      fileName: `batch-${ci + 1}`,
                    },
                  )

                  console.log('🗂️ [FolderUpload] Batch start response:', {
                    groupKey: clientParentDirKey,
                    chunkIndex: ci,
                    response: startResp,
                    timestamp: new Date().toISOString(),
                  })

                  // Optimistically add ghost files to cache for instant display
                  // This provides immediate UI feedback while backend files are being uploaded
                  if (
                    queryClient &&
                    !shouldSkipCacheUpdatesWithLog('both', 'batch file ghost upload')
                  ) {
                    // Add ghost files to cache in alphabetically sorted position
                    const { addItemToSortedCache } = await import('@qse/utils')

                    // Loop through each file in the batch response
                    startResp.files.forEach((fileResponse: any, fileIndex: number) => {
                      const actualFile = chunk[fileIndex] as File

                      const ghostFile = {
                        id: fileResponse.id,
                        name: fileResponse.name,
                        path: fileResponse.path || '',
                        parentId: folderId || '',
                        isDir: false,
                        status: fileResponse.status, // Will be 'UPLOADING'
                        sizeBytes: actualFile.size,
                        isFavorite: false,
                        mimeType: fileResponse.mimeType,
                        mediaType: fileResponse.mediaType,
                        createdAt: fileResponse.createdAt,
                        updatedAt: fileResponse.updatedAt,
                        trashedAt: null,
                        effectivelyTrashedBy: null,
                      }

                      const addGhostFileToData = (oldData: any) =>
                        addItemToSortedCache(oldData, ghostFile)

                      // Get ALL file list queries and update only the ones that match the file's parentId
                      const allFileQueries = queryClient.getQueriesData({
                        queryKey: queryKeys.files.lists(), // ['files', 'list']
                      })

                      allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
                        if (!cacheData) return

                        // Extract filter from query key: ['files', 'list', { parentId: 'xyz', ... }]
                        const queryFilter = queryKey[2] || {}
                        const queryParentId = queryFilter.parentId

                        // Check if this query should contain the file (matching parentId)
                        const shouldContainFile =
                          queryParentId === folderId ||
                          (queryParentId === null &&
                            (folderId === '' || folderId === null || folderId === undefined)) ||
                          (queryParentId === '' && (folderId === null || folderId === undefined)) ||
                          (queryParentId === undefined &&
                            (folderId === null || folderId === '' || folderId === undefined))

                        if (shouldContainFile) {
                          queryClient.setQueryData(queryKey, (oldData: any) =>
                            addGhostFileToData(oldData),
                          )
                        }
                      })
                    })

                    // Don't invalidate immediately - let the ghost files stay in cache to prevent UI flash
                    // The files will be updated when the upload completes or when the user navigates away
                  }
                } catch (error: any) {
                  // Handle batch start failure (e.g., destination folder deleted)
                  const is404Error =
                    error?.message?.includes('404') ||
                    error?.message?.toLowerCase().includes('not found') ||
                    error?.message?.toLowerCase().includes('deleted') ||
                    error?.status === 404 ||
                    error?.error?.status === 404

                  if (is404Error) {
                    console.log(
                      '🗑️ [FolderUpload] Batch start failed - destination folder deleted:',
                      {
                        groupKey: clientParentDirKey,
                        folderId,
                        chunkIndex: ci,
                        filesCount: chunk.length,
                        error: error.message,
                        timestamp: new Date().toISOString(),
                      },
                    )

                    // Mark only files that were destined for the deleted folder
                    // A chunk can contain files from multiple destinations
                    chunk.forEach((file: File & { path?: string }) => {
                      // Get the actual destination folder ID for this specific file
                      const fileParentDir =
                        getClientParentDir(file, rootOriginalName, serverFolderName) ||
                        rootServerPath
                      const fileParentDirKey = pathKey(fileParentDir)
                      const fileDestinationId =
                        pathToId.get(fileParentDirKey) ||
                        findMatchingPath(fileParentDirKey, pathToId)

                      // Only mark as failed if this file was destined for the deleted folder
                      if (fileDestinationId === folderId) {
                        markFileAsDeleted(file, folderId!)
                      } else {
                        console.log('🗂️ [FolderUpload] File not affected by deleted destination:', {
                          fileName: file.name,
                          fileDestinationId,
                          deletedFolderId: folderId,
                          timestamp: new Date().toISOString(),
                        })
                      }
                    })

                    // Add folder to deleted tracking set
                    get().addDeletedFolderId(folderId!)

                    // Continue to next chunk instead of throwing
                    continue
                  } else {
                    // For non-404 errors, rethrow to handle normally
                    console.error('🗂️ [FolderUpload] Batch start failed:', error)
                    throw error
                  }
                }

                const newFilesForChunk: EnhancedUploadingFile[] = chunk.map((f: any, i: number) => {
                  const name =
                    f.name ||
                    String(f.path || '')
                      .split('/')
                      .pop() ||
                    'unnamed'
                  const mimeType = f.type || getMimeTypeFromExtension(name)
                  const uploadStrategy =
                    payload.files[i].uploadType === 'MULTIPART' ? 'multipart' : 'single'

                  return {
                    ...makeUploadingFileEntry(
                      f,
                      name,
                      mimeType,
                      folderId!,
                      clientParentDirKey,
                      uploadStrategy,
                    ),
                    isHidden: true,
                  }
                })

                for (let i = 0; i < newFilesForChunk.length; i++) {
                  chunkPendingUploads.push({
                    actualFile: chunk[i] as File,
                    uploadingFile: newFilesForChunk[i],
                    serverHints: startResp?.files?.[i],
                  })
                }

                // Upload files in this chunk
                const chunkUploadTasks = chunkPendingUploads.map(
                  ({ actualFile, uploadingFile, serverHints }) =>
                    async () =>
                      new Promise<void>(resolve => {
                        // 🗑️ INDIVIDUAL FILE CHECK: Double-check destination exists before upload
                        if (isDestinationDeleted(folderId!)) {
                          console.log(
                            '🗑️ [FolderUpload] File destination deleted during queue, skipping upload:',
                            {
                              fileName: actualFile.name,
                              folderId,
                              timestamp: new Date().toISOString(),
                            },
                          )

                          // Mark file as failed
                          markFileAsDeleted(actualFile, folderId!)

                          // Add folder to deleted set
                          get().addDeletedFolderId(folderId!)

                          // Resolve immediately without uploading
                          resolve()

                          return
                        }

                        const abortController = new AbortController()

                        // Link individual file abort controller to parent folder abort controller
                        const parentFolderController =
                          get().folderAbortControllers.get(rootFolderId)

                        if (parentFolderController) {
                          // Listen for parent folder abort signal and propagate to individual file
                          parentFolderController.signal.addEventListener('abort', () => {
                            abortController.abort()
                          })
                        }

                        set((state: GlobalFileUploadState) => ({
                          abortControllers: new Map(state.abortControllers).set(
                            uploadingFile.id,
                            abortController,
                          ),
                        }))

                        const onUploadComplete = (completionData: any) => {
                          // Prevent duplicate IDs from being added
                          if (completedFileIds.has(completionData.id)) {
                            resolve()

                            return
                          }

                          completedFileIds.add(completionData.id)
                          chunkUploadCompletions.push(completionData)
                          set((state: GlobalFileUploadState) => ({
                            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
                              if (f.isFolderUpload && f.folderUploadId === rootFolderId) {
                                const newUploadedFiles = (f.uploadedFiles || 0) + 1
                                const formattedName = formatFolderProgress(
                                  f.originalName,
                                  f.serverName,
                                )

                                return {
                                  ...f,
                                  name: formattedName,
                                  uploadedFiles: newUploadedFiles,
                                  // Don't override FAILED status - keep current status
                                  status:
                                    f.status === FileStatus.FAILED
                                      ? f.status
                                      : FileStatus.UPLOADING,
                                }
                              }

                              return f
                            }),
                          }))
                          set((state: GlobalFileUploadState) => {
                            const m = new Map(state.abortControllers)

                            m.delete(uploadingFile.id)

                            return { abortControllers: m }
                          })

                          // Check if all files in this folder are complete and clean up folder controller
                          const remainingFilesInFolder = get().uploadingFiles.filter(
                            f =>
                              f.isFolderUpload &&
                              f.folderUploadId === rootFolderId &&
                              f.status === FileStatus.UPLOADING,
                          )

                          if (remainingFilesInFolder.length === 0) {
                            console.log(
                              `All files in folder ${rootFolderId} completed, cleaning up folder controller`,
                            )
                            set((state: GlobalFileUploadState) => {
                              const folderControllers = new Map(state.folderAbortControllers)

                              folderControllers.delete(rootFolderId)

                              return { folderAbortControllers: folderControllers }
                            })
                          }
                          resolve()
                        }

                        const onError = (error: Error) => {
                          console.error('Upload failed:', error)

                          // Check if this upload was cancelled by the user
                          const currentFile = get().uploadingFiles.find(
                            (f: EnhancedUploadingFile) => f.id === uploadingFile.id,
                          )
                          const isGloballyAborted = get().isAborted
                          const isFileCancelled =
                            currentFile?.errorMessage === 'Upload cancelled' ||
                            currentFile?.errorMessage === 'Cancelling upload...' ||
                            currentFile?.isCancelling

                          // Also check if the abort controller signal has been triggered
                          const isAbortSignalTriggered = abortController?.signal?.aborted

                          // If the file was cancelled or globally aborted, suppress all error messages
                          if (isGloballyAborted || isFileCancelled || isAbortSignalTriggered) {
                            console.log(
                              'Suppressing error message for cancelled upload:',
                              uploadingFile.name,
                            )

                            set((state: GlobalFileUploadState) => {
                              const m = new Map(state.abortControllers)

                              m.delete(uploadingFile.id)

                              return { abortControllers: m }
                            })

                            resolve()

                            return
                          }

                          let errorMessage = 'Upload failed'
                          let isUserCancelled = false
                          let isGracefulFailure = false
                          let failureReason: 'upload' | 'encryption' | 'deleted_destination' =
                            'upload'

                          if (
                            error.message.includes('Upload aborted') ||
                            error.message.includes('Chunk upload aborted') ||
                            error.message.includes('upload aborted') ||
                            error.name === 'AbortError'
                          ) {
                            errorMessage = 'Upload cancelled'
                            isUserCancelled = true
                          } else if (error.message.includes('403')) {
                            errorMessage =
                              'Upload completion failed: Access denied. This might be due to token expiration or S3 processing delay. Please try again.'
                          } else if (error.message.includes('401')) {
                            errorMessage = 'Authentication error - please log in again'
                          } else if (
                            error.message.includes('429') ||
                            error.message.includes('exceeded')
                          ) {
                            errorMessage =
                              'You have exceeded your personal storage quota. Free up space or contact your admin.'
                          } else if (
                            error.message.includes('400') &&
                            error.message.includes('quota not configured')
                          ) {
                            errorMessage = 'User quota not configured. Please contact your admin.'
                          } else if (
                            error.message.toLowerCase().includes('not found') ||
                            error.message.toLowerCase().includes('parent') ||
                            error.message.toLowerCase().includes('deleted') ||
                            error.message.toLowerCase().includes('no longer exists') ||
                            error.message.includes('404')
                          ) {
                            // Graceful failure - destination folder was permanently deleted
                            failureReason = 'deleted_destination'
                            isGracefulFailure = true
                            errorMessage = 'Destination folder no longer exists'

                            // 🗑️ Add folder to deleted tracking set to prevent further upload attempts
                            console.log(
                              '🗑️ [FolderUpload] Detected deleted destination from API error:',
                              {
                                fileName: actualFile.name,
                                folderId: folderId!,
                                errorMessage: error.message,
                                timestamp: new Date().toISOString(),
                              },
                            )
                            get().addDeletedFolderId(folderId!)

                            // 🗑️ Abort any in-flight uploads to the same destination OR any child destinations
                            // Find all pending uploads for this folder and its children
                            const allAbortControllers = get().abortControllers
                            const uploadingFilesForFolder = get().uploadingFiles.filter(
                              (f: EnhancedUploadingFile) => {
                                if (
                                  !f.isFolderUpload ||
                                  f.folderUploadId !== rootFolderId ||
                                  f.status !== FileStatus.UPLOADING
                                ) {
                                  return false
                                }

                                // Check if this file's destination is the deleted folder or any of its children
                                return isDestinationDeleted(f.parentId || '')
                              },
                            )

                            console.log(
                              '🗑️ [FolderUpload] Aborting in-flight uploads to deleted folder and its children:',
                              {
                                folderId: folderId!,
                                filesCount: uploadingFilesForFolder.length,
                                fileNames: uploadingFilesForFolder.map(f => f.name),
                                timestamp: new Date().toISOString(),
                              },
                            )

                            uploadingFilesForFolder.forEach(f => {
                              const controller = allAbortControllers.get(f.id)

                              if (controller) {
                                controller.abort()
                                console.log('🗑️ [FolderUpload] Aborted upload:', f.name)
                              }
                            })
                          } else if (
                            error.message.toLowerCase().includes('notfounderror') ||
                            error.message.toLowerCase().includes('notreadableerror') ||
                            error.message
                              .toLowerCase()
                              .includes('file has been moved or deleted') ||
                            error.message
                              .toLowerCase()
                              .includes('a requested file or directory could not be found') ||
                            error.message.includes('no longer accessible') ||
                            error.message.includes('may have been deleted') ||
                            error.name === 'NotFoundError' ||
                            error.name === 'NotReadableError' ||
                            (error as any).code === 'ENOENT' ||
                            error.message.includes('FileReader') ||
                            error.message.includes('not readable') ||
                            error.message.includes('The operation could not be completed')
                          ) {
                            // File was deleted from local filesystem
                            failureReason = 'upload'
                            isGracefulFailure = true
                            errorMessage = `File "${actualFile.name}" was removed from your computer during upload`

                            // Track this file for backend cancellation
                            const fileId = serverHints?.id || uploadingFile.serverFileId

                            if (fileId && !deletedFileIds.includes(fileId)) {
                              console.log('📝 [FolderUpload] Tracking deleted file for abort:', {
                                fileName: actualFile.name,
                                fileId: fileId,
                              })
                              deletedFileIds.push(fileId)
                            }
                          } else if (error.message.includes('chunk')) {
                            errorMessage = 'Chunk upload failed - please try again'
                          } else if (error.message.includes('timeout')) {
                            errorMessage =
                              'Upload timeout - please check your connection and try again'
                          } else if (
                            error.message.toLowerCase().includes('network') ||
                            error.message.includes('Failed to fetch') ||
                            error.message.includes('TypeError')
                          ) {
                            // For folder uploads, "Network error" is most likely a file deletion
                            // because we're reading from local filesystem. If the signed URL and API
                            // are working (which they are, since we got this far), then a network error
                            // during upload usually means the source file is no longer accessible.
                            failureReason = 'upload'
                            isGracefulFailure = true
                            errorMessage = `File "${actualFile.name}" was removed from your computer during upload`

                            // Track this file for backend cancellation
                            const fileId = serverHints?.id || uploadingFile.serverFileId

                            if (fileId && !deletedFileIds.includes(fileId)) {
                              console.log('📝 [FolderUpload] Tracking deleted file for abort:', {
                                fileName: actualFile.name,
                                fileId: fileId,
                              })
                              deletedFileIds.push(fileId)
                            }
                          }

                          // Show appropriate toast messages
                          if (!isUserCancelled) {
                            if (isGracefulFailure) {
                              // For graceful failures (deleted files), don't show individual toasts
                              // They will be batched at folder level to avoid spam
                              console.log('🗑️ [FolderUpload] Graceful failure (no toast):', {
                                fileName: actualFile.name,
                                reason: failureReason,
                                errorMessage,
                                timestamp: new Date().toISOString(),
                              })
                            } else {
                              // For real errors, show error toast
                              toast.error(errorMessage)
                            }
                          }

                          // Create failed file detail
                          // [RETRY_FAILED] Attach originalFile ref + parentId for retry capability
                          const failedFileDetail: FailedFileDetail = {
                            name: actualFile.name,
                            path: (actualFile as any).path || actualFile.name,
                            reason: failureReason,
                            file: actualFile,
                            parentId: uploadingFile.parentId,
                          }

                          // Remove failed ghost file from cache (files deleted from local filesystem)
                          // These files never reach the server, so they should disappear from UI
                          if (isGracefulFailure && uploadingFile.serverFileId && queryClient) {
                            const fileParentId = uploadingFile.parentId

                            const removeFileFromData = (oldData: any) => {
                              if (!oldData) return oldData

                              if (oldData.pages) {
                                return {
                                  ...oldData,
                                  pages: oldData.pages.map((page: any) => ({
                                    ...page,
                                    data: (page.data || []).filter(
                                      (f: any) => f.id !== uploadingFile.serverFileId,
                                    ),
                                    totalCount: Math.max(0, (page.totalCount || 0) - 1),
                                  })),
                                }
                              }

                              if (oldData.data) {
                                return {
                                  ...oldData,
                                  data: (oldData.data || []).filter(
                                    (f: any) => f.id !== uploadingFile.serverFileId,
                                  ),
                                  totalCount: Math.max(0, (oldData.totalCount || 0) - 1),
                                }
                              }

                              return oldData
                            }

                            // Get ALL file list queries and remove from only the ones that match the file's parentId
                            const allFileQueries = queryClient.getQueriesData({
                              queryKey: queryKeys.files.lists(), // ['files', 'list']
                            })

                            allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
                              if (!cacheData) return

                              // Extract filter from query key: ['files', 'list', { parentId: 'xyz', ... }]
                              const queryFilter = queryKey[2] || {}
                              const queryParentId = queryFilter.parentId

                              // Check if this query should contain the file (matching parentId)
                              const shouldContainFile =
                                queryParentId === fileParentId ||
                                (queryParentId === null &&
                                  (fileParentId === '' ||
                                    fileParentId === null ||
                                    fileParentId === undefined)) ||
                                (queryParentId === '' &&
                                  (fileParentId === null || fileParentId === undefined)) ||
                                (queryParentId === undefined &&
                                  (fileParentId === null ||
                                    fileParentId === '' ||
                                    fileParentId === undefined))

                              if (shouldContainFile) {
                                queryClient.setQueryData(queryKey, (oldData: any) =>
                                  removeFileFromData(oldData),
                                )
                              }
                            })
                          }

                          set((state: GlobalFileUploadState) => ({
                            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
                              if (f.isFolderUpload && f.folderUploadId === rootFolderId) {
                                const newFailedUploads = (f.failedUploads || 0) + 1
                                const newFailedFiles = (f.failedFiles || 0) + 1
                                const totalFiles = f.totalFiles || 0
                                const uploadedFiles = f.uploadedFiles || 0
                                const formattedName = formatFolderProgress(
                                  f.originalName,
                                  f.serverName,
                                )

                                // Add failed file details
                                const updatedFailedFileDetails = addOrUpdateFailedFileDetail(
                                  f.failedFileDetails,
                                  failedFileDetail,
                                )

                                // Check if all files have failed or completed
                                const allFilesProcessed =
                                  uploadedFiles + newFailedUploads >= totalFiles

                                // If ALL files failed (100% failure - destination deleted scenario)
                                const allFilesFailed =
                                  newFailedUploads >= totalFiles && uploadedFiles === 0

                                // Determine new status
                                let newStatus = f.status

                                if (allFilesFailed) {
                                  // Complete failure - mark as FAILED
                                  newStatus = FileStatus.FAILED
                                  // Show error toast for complete failure
                                  ;(async () => {
                                    const { toast } = await import('sonner')
                                    const folderName = f.serverName || f.originalName || 'Folder'

                                    toast.error(
                                      `"${folderName}" upload failed. All ${totalFiles} files failed to upload.`,
                                      {
                                        description: isGracefulFailure
                                          ? 'Destination folder was deleted or moved during upload'
                                          : 'Files could not be uploaded',
                                        duration: 10000,
                                      },
                                    )

                                    // Remove failed folder from file explorer if destination was deleted
                                    if (isGracefulFailure && queryClient) {
                                      const { queryKeys } = await import('@qse/api')
                                      const filesListKey = queryKeys.files.lists()
                                      const queriesToUpdate = queryClient.getQueriesData({
                                        queryKey: filesListKey,
                                      })

                                      queriesToUpdate.forEach(
                                        ([queryKey, cacheData]: [any, any]) => {
                                          if (!cacheData) return

                                          // Remove folder from cache
                                          if (cacheData.pages && Array.isArray(cacheData.pages)) {
                                            queryClient.setQueryData(queryKey, (oldData: any) => {
                                              if (!oldData?.pages) return oldData

                                              const updatedPages = oldData.pages.map(
                                                (page: any) => {
                                                  if (!page.data) return page

                                                  return {
                                                    ...page,
                                                    data: page.data.filter(
                                                      (item: any) => item.id !== rootFolderId,
                                                    ),
                                                    totalCount: Math.max(
                                                      0,
                                                      (page.totalCount || 0) - 1,
                                                    ),
                                                  }
                                                },
                                              )

                                              return {
                                                ...oldData,
                                                pages: updatedPages,
                                              }
                                            })
                                          } else if (
                                            cacheData.data &&
                                            Array.isArray(cacheData.data)
                                          ) {
                                            queryClient.setQueryData(queryKey, (oldData: any) => {
                                              if (!oldData?.data) return oldData

                                              return {
                                                ...oldData,
                                                data: oldData.data.filter(
                                                  (item: any) => item.id !== rootFolderId,
                                                ),
                                                totalCount: Math.max(
                                                  0,
                                                  (oldData.totalCount || 0) - 1,
                                                ),
                                              }
                                            })
                                          }
                                        },
                                      )
                                    }
                                  })()
                                } else if (allFilesProcessed) {
                                  // All files processed (uploaded or failed)
                                  // If some succeeded, move to ENCRYPTING phase
                                  // Don't mark as AVAILABLE yet - wait for encryption
                                  // BUT: Don't override if folder is already marked as FAILED
                                  if (uploadedFiles > 0 && f.status !== FileStatus.FAILED) {
                                    newStatus = FileStatus.ENCRYPTING
                                  }
                                }

                                return {
                                  ...f,
                                  name: formattedName,
                                  status: newStatus,
                                  // Don't set progress to 100 yet - let WebSocket handle final progress
                                  failedFiles: newFailedFiles,
                                  failedUploads: newFailedUploads,
                                  failedFileDetails: updatedFailedFileDetails,
                                }
                              }

                              return f
                            }),
                          }))

                          set((state: GlobalFileUploadState) => {
                            const m = new Map(state.abortControllers)

                            m.delete(uploadingFile.id)

                            return { abortControllers: m }
                          })

                          resolve() // خليه يكمّل باقي المهام حتى لو فشل ملف
                        }

                        ;(async () => {
                          try {
                            if (get().isAborted) return resolve()

                            const serverHints = chunkPendingUploads.find(
                              p => p.uploadingFile.id === uploadingFile.id,
                            )?.serverHints

                            if (!serverHints)
                              throw new Error('No server hints found for file upload')

                            if (uploadingFile.uploadStrategy === 'multipart') {
                              if (!serverHints.parts || serverHints.parts.length === 0)
                                throw new Error('No multipart URLs found for file')
                              const parts = splitFileIntoChunks(actualFile)
                              const etags: string[] = []

                              // Import retry utilities for S3 uploads
                              const { retryS3Upload } = await import('@qse/utils')

                              for (let i = 0; i < parts.length; i++) {
                                if (abortController.signal.aborted)
                                  throw new Error('Upload aborted')
                                const partUrl = serverHints.parts[i]?.url

                                if (!partUrl) throw new Error(`No URL found for part ${i + 1}`)

                                const etag = await retryS3Upload(
                                  () =>
                                    uploadChunkToSignedUrl(
                                      partUrl,
                                      parts[i],
                                      undefined,
                                      abortController,
                                      () => {},
                                    ),
                                  {
                                    fileName: actualFile.name,
                                    chunkIndex: i,
                                    totalChunks: parts.length,
                                  },
                                )

                                etags[i] = etag
                              }
                              onUploadComplete({
                                id: serverHints.id,
                                uploadType: 'MULTIPART' as const,
                                parts: etags.map((ETag, idx) => ({ ETag, PartNumber: idx + 1 })),
                              })
                            } else {
                              if (!serverHints.singleUrl)
                                throw new Error('No single upload URL found for file')

                              // Import retry utilities for S3 uploads
                              const { retryS3Upload } = await import('@qse/utils')

                              await retryS3Upload(
                                () =>
                                  uploadToSignedUrlWithDebug(
                                    serverHints.singleUrl,
                                    actualFile,
                                    undefined,
                                    abortController,
                                    () => {},
                                  ),
                                {
                                  fileName: actualFile.name,
                                },
                              )
                              onUploadComplete({
                                id: serverHints.id,
                                uploadType: 'SINGLE' as const,
                                sizeBytes: actualFile.size,
                              })
                            }
                          } catch (e) {
                            onError(e as Error)
                          }
                        })()
                      }),
                )

                console.log('🗂️ [FolderUpload] Starting upload tasks for chunk:', {
                  groupKey: clientParentDirKey,
                  chunkIndex: ci,
                  totalTasks: chunkUploadTasks.length,
                  maxParallel: chunkUploadTasks.length, // Upload all 5 files concurrently
                  timestamp: new Date().toISOString(),
                })

                // Upload exactly 5 files at a time (all files in this chunk)
                await runWithConcurrency(chunkUploadTasks, chunkUploadTasks.length)
                console.log('🗂️ [FolderUpload] Chunk upload finished:', {
                  groupKey: clientParentDirKey,
                  chunkIndex: ci,
                  totalTasks: chunkUploadTasks.length,
                  timestamp: new Date().toISOString(),
                })

                // Call complete-batch after each chunk of 5 files is uploaded
                if (chunkUploadCompletions.length > 0) {
                  if (get().isAborted) return
                  try {
                    console.log('🗂️ [FolderUpload] Completing uploads for chunk:', {
                      groupKey: clientParentDirKey,
                      chunkIndex: ci,
                      completionsCount: chunkUploadCompletions.length,
                      completions: chunkUploadCompletions.map(c => ({
                        id: c.id,
                        uploadType: c.uploadType,
                        sizeBytes: c.sizeBytes,
                      })),
                      timestamp: new Date().toISOString(),
                    })

                    const resp = await retryFolderUploadApi(
                      () =>
                        bulkCompleteUploads(rootFolderId, {
                          completions: chunkUploadCompletions,
                        }),
                      {
                        operation: 'bulkCompleteUploads',
                        folderId: rootFolderId,
                        fileName: `batch-${ci + 1}`,
                      },
                    )

                    console.log('🗂️ [FolderUpload] Batch completion response for chunk:', {
                      groupKey: clientParentDirKey,
                      chunkIndex: ci,
                      response: resp,
                      timestamp: new Date().toISOString(),
                    })

                    // تحديث الداتا بعد الإكمال
                    const { driveFiles } = get()
                    const newDriveFiles: FileItem[] = chunkUploadCompletions.map(completion => {
                      const pu = chunkPendingUploads.find(p => p.serverHints?.id === completion.id)

                      return {
                        id: parseInt(completion.id),
                        name: pu?.uploadingFile?.name || pu?.actualFile?.name || 'unknown',
                        type:
                          pu?.actualFile?.type ||
                          getMimeTypeFromExtension(pu?.uploadingFile?.name || 'unknown'),
                        lastModified: new Date().toLocaleDateString(),
                        path: '', // backend سيملؤه
                        createdAt: new Date().toLocaleDateString(),
                        mimeType:
                          pu?.actualFile?.type ||
                          getMimeTypeFromExtension(pu?.uploadingFile?.name || 'unknown'),
                      }
                    })

                    set(() => ({ driveFiles: [...driveFiles, ...newDriveFiles] }))

                    if (queryClient) {
                      // Update cache directly instead of invalidating
                      const filesListKey = queryKeys.files.lists()

                      // Get all queries that match the files list pattern
                      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

                      queriesToUpdate.forEach(([queryKey]: [any, any]) => {
                        queryClient.setQueryData(queryKey, (oldData: any) => {
                          if (!oldData || !Array.isArray(oldData.data)) {
                            return oldData
                          }

                          return {
                            ...oldData,
                            data: [...oldData.data, ...newDriveFiles],
                            totalCount: (oldData.totalCount || 0) + newDriveFiles.length,
                          }
                        })
                      })
                    }
                  } catch (error) {
                    console.error('🗂️ [FolderUpload] Batch completion failed for chunk:', {
                      groupKey: clientParentDirKey,
                      chunkIndex: ci,
                      error,
                    })

                    // Check if upload is being cancelled or aborted
                    const isGloballyAborted = get().isAborted
                    const folderUpload = get().uploadingFiles.find(
                      f => f.isFolderUpload && f.folderUploadId === rootFolderId,
                    )
                    const isFolderCancelling =
                      folderUpload?.isCancelling ||
                      folderUpload?.errorMessage === 'Upload cancelled'

                    // Check for cancellation/abort errors in the error message
                    const errorMessage =
                      error instanceof Error
                        ? error.message.toLowerCase()
                        : String(error).toLowerCase()
                    const isCancelledError =
                      errorMessage.includes('cancelled') ||
                      errorMessage.includes('canceled') ||
                      errorMessage.includes('aborted') ||
                      errorMessage.includes('abort')

                    // If upload is being cancelled, don't show error toast
                    if (isGloballyAborted || isFolderCancelling || isCancelledError) {
                      console.log('Suppressing batch completion error for cancelled upload')

                      return
                    }

                    toast.error('Failed to complete upload batch')
                  }
                }
              }
            }

            console.log('🗂️ [FolderUpload] Folder upload processing completed:', {
              rootFolderId,
              rootOriginalName,
              totalGroupsProcessed: groups.size,
              timestamp: new Date().toISOString(),
            })

            // Mark folder upload as entering encryption phase
            // Don't mark as AVAILABLE yet - wait for WebSocket events to confirm encryption is complete
            const folderUpload = get().uploadingFiles.find(
              f => f.isFolderUpload && f.folderUploadId === rootFolderId,
            )

            if (folderUpload) {
              // Update folder status to ENCRYPTING (upload phase complete, encryption in progress)
              // WebSocket events will update to AVAILABLE when encryption completes
              set((state: GlobalFileUploadState) => ({
                uploadingFiles: state.uploadingFiles
                  .map((f: EnhancedUploadingFile) => {
                    if (f.isFolderUpload && f.folderUploadId === rootFolderId) {
                      return {
                        ...f,
                        // Don't override FAILED status - keep current status
                        status: f.status === FileStatus.FAILED ? f.status : FileStatus.ENCRYPTING,
                        // Keep current progress, don't set to 100% yet
                        // WebSocket will update progress during encryption phase
                      }
                    }

                    return f
                  })
                  // Remove hidden individual files from this folder upload
                  .filter(
                    (f: EnhancedUploadingFile) =>
                      !(f.isHidden && f.isFolderUpload && f.folderUploadId === rootFolderId),
                  ),
              }))

              // 🚀 IMMEDIATE CACHE UPDATE: Update React Query cache immediately for FileCard
              // This ensures FileCard reflects the status change instantly, not just when WebSocket events arrive
              if (folderUpload.serverFileId && queryClient) {
                get().updateFileStatusFromWebSocket(
                  folderUpload.serverFileId,
                  folderUpload.status === FileStatus.FAILED
                    ? folderUpload.status
                    : FileStatus.ENCRYPTING,
                  queryClient,
                )
              }

              // Don't show completion toast here - wait for WebSocket to confirm encryption is done
              // The toast will be shown by updateFolderUploadProgressFromWebSocket when status becomes AVAILABLE

              // Send bulk abort request for deleted files
              if (deletedFileIds.length > 0) {
                console.log('🗂️ [FolderUpload] Sending bulk abort for deleted files:', {
                  count: deletedFileIds.length,
                  fileIds: deletedFileIds,
                })

                // Import and call bulk abort API
                ;(async () => {
                  try {
                    const { bulkAbortUploads } = await import('@qse/api')

                    await bulkAbortUploads({ fileIds: deletedFileIds })
                    console.log(
                      '✅ [FolderUpload] Successfully aborted deleted files on backend:',
                      {
                        count: deletedFileIds.length,
                      },
                    )
                  } catch (err) {
                    console.error(
                      '❌ [FolderUpload] Failed to abort deleted files on backend:',
                      err,
                    )
                    // Don't show error to user - this is a cleanup operation
                  }
                })()
              }
            }

            get().removeBeforeUnloadListener?.()

            return
          } catch (err) {
            console.error('Failed to initiate folder upload:', err)

            // Check if upload is being cancelled or aborted
            const isGloballyAborted = get().isAborted
            const folderUpload = get().uploadingFiles.find(
              f => f.isFolderUpload && f.folderUploadId === rootFolderId,
            )
            const isFolderCancelling =
              folderUpload?.isCancelling || folderUpload?.errorMessage === 'Upload cancelled'

            // Check for cancellation/abort errors in the error message
            const errorMessage =
              err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase()
            const isCancelledError =
              errorMessage.includes('cancelled') ||
              errorMessage.includes('canceled') ||
              errorMessage.includes('aborted') ||
              errorMessage.includes('abort')

            // If upload is being cancelled, don't show error toast
            if (isGloballyAborted || isFolderCancelling || isCancelledError) {
              console.log('Suppressing folder upload error for cancelled upload')

              return
            }

            toast.error(err instanceof Error ? err.message : String(err))

            return
          }
        }

        // ───────────────────────────────
        // مسار: رفع ملفات عادي (non-folder)
        // ───────────────────────────────

        const validation = validateFilesForUpload(sanitizedFiles)

        if (validation.validationErrors.length > 0) {
          validation.validationErrors.forEach(error => {
            toast.error(error, { position: 'top-right' })
          })
          if (validation.validFiles.length === 0) return
        }

        const newFilesToUpload = validation.validFiles as (File & { path?: string })[]

        if (newFilesToUpload.length === 0) {
          console.log('All files are already being uploaded, skipping duplicate upload')

          return
        }

        const newFiles: EnhancedUploadingFile[] = newFilesToUpload.map(file => {
          const uploadStrategy = file.size >= MULTIPART_THRESHOLD ? 'multipart' : 'single'
          const mimeType = file.type || getMimeTypeFromExtension(file.name || 'unknown')

          return {
            id: Date.now() + Math.random(),
            name: file.name || 'unknown',
            originalName: file.name || 'unknown',
            progress: 0,
            status: FileStatus.UPLOADING,
            type: mimeType,
            path: file.path ?? '',
            parentId,
            parentPath,
            originalFile: file,
            uploadStrategy,
            mediaType: getMediaTypeByExtension(file.name),
            mimeType,
          }
        })

        set((state: GlobalFileUploadState) => ({
          uploadingFiles: [...newFiles, ...state.uploadingFiles],
          isUploadModalOpen: true,
          isAborted: false,
        }))

        get().setupBeforeUnloadListener()

        // نفس منطق الـ batching السابق عندك
        const processFilesInBatches = async () => {
          const { batchSize = BATCH_SIZE, batchDelay = BATCH_DELAY } = get()
          const batches: EnhancedUploadingFile[][] = []

          for (let i = 0; i < newFiles.length; i += batchSize) {
            batches.push(newFiles.slice(i, i + batchSize))
          }

          for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            const batch = batches[batchIndex]

            if (get().isAborted) break

            console.log(
              `📦 Processing batch ${batchIndex + 1}/${batches.length} with ${batch.length} files`,
            )
            batch.forEach(file => {
              file.batchIndex = batchIndex
            })

            const batchPromises = batch.map(async uploadingFile => {
              const actualFile = uploadingFile.originalFile

              if (!actualFile) return

              const abortController = new AbortController()

              set((state: GlobalFileUploadState) => ({
                abortControllers: new Map(state.abortControllers).set(
                  uploadingFile.id,
                  abortController,
                ),
              }))

              const updateProgress = (progress: number) => {
                if (Math.abs(progress - uploadingFile.progress) >= 5 || progress === 100) {
                  set((state: GlobalFileUploadState) => ({
                    uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                      f.id === uploadingFile.id ? { ...f, progress } : f,
                    ),
                  }))
                }
              }
              const updateChunkInfo = (currentChunk: number, totalChunks: number) => {
                set((state: GlobalFileUploadState) => ({
                  uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                    f.id === uploadingFile.id ? { ...f, currentChunk, chunks: totalChunks } : f,
                  ),
                }))
              }

              const onComplete = (fileFromApi: any) => {
                const { driveFiles } = get()
                const newDriveFile: FileItem = {
                  id: parseInt(fileFromApi.id),
                  name: fileFromApi.name || actualFile.name || 'unknown',
                  type: actualFile.type || getMimeTypeFromExtension(actualFile.name || 'unknown'),
                  lastModified: new Date(fileFromApi.updatedAt).toLocaleDateString(),
                  path: fileFromApi.path,
                  createdAt: new Date(fileFromApi.createdAt).toLocaleDateString(),
                  mimeType:
                    actualFile.type || getMimeTypeFromExtension(actualFile.name || 'unknown'),
                }

                set((state: GlobalFileUploadState) => ({
                  uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                    f.id === uploadingFile.id
                      ? {
                          ...f,
                          status: fileFromApi.status as FileStatus,
                          progress: 100,
                          name: fileFromApi.name ?? f.name,
                          originalName: f.originalName ?? f.name,
                          path: fileFromApi.path ?? f.path,
                          serverFileId: fileFromApi.id ?? f.serverFileId,
                        }
                      : f,
                  ),
                  driveFiles: [...driveFiles, newDriveFile],
                }))

                // 🚀 IMMEDIATE CACHE UPDATE: Update React Query cache immediately for FileCard
                // This ensures FileCard reflects the status change instantly (UPLOADING -> ENCRYPTING -> AVAILABLE)
                if (fileFromApi.id && queryClient) {
                  get().updateFileStatusFromWebSocket(
                    fileFromApi.id,
                    fileFromApi.status as FileStatus,
                    queryClient,
                  )
                }

                const keysToInvalidate = invalidateQueryKeys?.length
                  ? invalidateQueryKeys
                  : [queryKeys.files.lists()]

                if (queryClient && !shouldSkipCacheUpdatesWithLog('both', 'folder creation')) {
                  // Update cache directly instead of invalidating
                  ;(keysToInvalidate as any[]).forEach(async (key: any) => {
                    const queriesToUpdate = queryClient.getQueriesData({ queryKey: key })
                    const { insertSorted } = await import('@qse/utils')

                    queriesToUpdate.forEach(([queryKey]: [any, any]) => {
                      queryClient.setQueryData(queryKey, (oldData: any) => {
                        if (!oldData || !Array.isArray(oldData.data)) {
                          return oldData
                        }

                        const fileExists = oldData.data.some(
                          (f: any) =>
                            f.id === newDriveFile.id ||
                            (f.name === newDriveFile.name && f.path === newDriveFile.path),
                        )

                        if (!fileExists) {
                          return {
                            ...oldData,
                            data: insertSorted(oldData.data, newDriveFile),
                            totalCount: (oldData.totalCount || 0) + 1,
                          }
                        }

                        return {
                          ...oldData,
                          data: oldData.data.map((f: any) =>
                            f.id === newDriveFile.id ||
                            (f.name === newDriveFile.name && f.path === newDriveFile.path)
                              ? { ...f, ...newDriveFile }
                              : f,
                          ),
                        }
                      })
                    })
                  })
                }

                // Update folder upload progress if this is a folder upload
                if (uploadingFile.isFolderUpload && uploadingFile.folderUploadId) {
                  set((state: GlobalFileUploadState) => ({
                    uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
                      if (f.isFolderUpload && f.folderUploadId === uploadingFile.folderUploadId) {
                        const newUploadedFiles = (f.uploadedFiles || 0) + 1
                        const totalFiles = f.totalFiles || 0
                        const progressPercentage =
                          totalFiles > 0 ? Math.round((newUploadedFiles / totalFiles) * 100) : 0

                        return {
                          ...f,
                          uploadedFiles: newUploadedFiles,
                          progress: Math.max(f.progress || 0, progressPercentage),
                        }
                      }

                      return f
                    }),
                  }))
                }

                set((state: GlobalFileUploadState) => {
                  const newControllers = new Map(state.abortControllers)

                  newControllers.delete(uploadingFile.id)

                  return { abortControllers: newControllers }
                })

                const allComplete = get().uploadingFiles.every(
                  (f: EnhancedUploadingFile) =>
                    f.status === FileStatus.AVAILABLE || f.status === FileStatus.FAILED,
                )

                if (allComplete) get().removeBeforeUnloadListener()
              }

              const onError = (error: Error) => {
                console.error('Upload failed:', error)

                // Check if this upload was cancelled by the user
                const currentFile = get().uploadingFiles.find(
                  (f: EnhancedUploadingFile) => f.id === uploadingFile.id,
                )
                const isGloballyAborted = get().isAborted
                const isFileCancelled =
                  currentFile?.errorMessage === 'Upload cancelled' ||
                  currentFile?.errorMessage === 'Cancelling upload...' ||
                  currentFile?.isCancelling

                // Also check if the abort controller signal has been triggered
                const isAbortSignalTriggered = abortController?.signal?.aborted

                // If the file was cancelled or globally aborted, suppress all error messages
                if (isGloballyAborted || isFileCancelled || isAbortSignalTriggered) {
                  console.log('Suppressing error message for cancelled upload:', uploadingFile.name)

                  set((state: GlobalFileUploadState) => {
                    const newControllers = new Map(state.abortControllers)

                    newControllers.delete(uploadingFile.id)

                    return { abortControllers: newControllers }
                  })

                  const allComplete = get().uploadingFiles.every(
                    (f: EnhancedUploadingFile) =>
                      f.status === FileStatus.AVAILABLE || f.status === FileStatus.FAILED,
                  )

                  if (allComplete) get().removeBeforeUnloadListener()

                  return
                }

                let errorMessage = 'Upload failed'
                const msg = error.message
                const isAbortError =
                  msg.includes('Upload aborted') ||
                  msg.includes('Chunk upload aborted') ||
                  msg.includes('upload aborted') ||
                  msg.includes('canceled') ||
                  msg.includes('cancelled') ||
                  (error as any).name === 'AbortError' ||
                  (error as any).name === 'CanceledError' ||
                  (error as any).code === 'ERR_CANCELED'

                if (isAbortError) {
                  errorMessage = 'Upload cancelled'
                } else if (msg.includes('403')) {
                  errorMessage =
                    'Upload completion failed: Access denied. This might be due to token expiration or S3 processing delay. Please try again.'
                } else if (
                  msg.toLowerCase().includes('network') ||
                  msg.includes('Failed to fetch') ||
                  msg.includes('TypeError')
                ) {
                  errorMessage = 'Network error - please check your connection'
                } else if (msg.includes('401')) {
                  errorMessage = 'Authentication error - please log in again'
                } else if (msg.includes('timeout')) {
                  errorMessage = 'Upload timeout - please check your connection and try again'
                } else if (msg.includes('429') || msg.includes('exceeded')) {
                  errorMessage =
                    'You have exceeded your personal storage quota. Free up space or contact your admin.'
                } else if (msg.includes('400') && msg.includes('quota not configured')) {
                  errorMessage = 'User quota not configured. Please contact your admin.'
                }

                set((state: GlobalFileUploadState) => ({
                  uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                    f.id === uploadingFile.id
                      ? { ...f, status: FileStatus.FAILED, errorMessage }
                      : f,
                  ),
                }))

                // Update folder upload progress if this is a folder upload
                if (uploadingFile.isFolderUpload && uploadingFile.folderUploadId) {
                  set((state: GlobalFileUploadState) => ({
                    uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
                      if (f.isFolderUpload && f.folderUploadId === uploadingFile.folderUploadId) {
                        const newFailedUploads = (f.failedUploads || 0) + 1
                        const totalFiles = f.totalFiles || 0
                        const uploadedFiles = f.uploadedFiles || 0
                        const totalProcessed = uploadedFiles + newFailedUploads
                        const progressPercentage =
                          totalFiles > 0 ? Math.round((totalProcessed / totalFiles) * 100) : 0

                        return {
                          ...f,
                          failedUploads: newFailedUploads,
                          failedFiles: newFailedUploads + (f.failedEncryptions || 0),
                          progress: Math.max(f.progress || 0, progressPercentage),
                        }
                      }

                      return f
                    }),
                  }))
                }

                set((state: GlobalFileUploadState) => {
                  const newControllers = new Map(state.abortControllers)

                  newControllers.delete(uploadingFile.id)

                  return { abortControllers: newControllers }
                })

                const allComplete = get().uploadingFiles.every(
                  (f: EnhancedUploadingFile) =>
                    f.status === FileStatus.AVAILABLE || f.status === FileStatus.FAILED,
                )

                if (allComplete) get().removeBeforeUnloadListener()
              }

              try {
                if (get().isAborted) return
                if (uploadingFile.uploadStrategy === 'multipart') {
                  await handleMultipartFileUpload(
                    actualFile,
                    uploadingFile,
                    updateProgress,
                    updateChunkInfo,
                    onComplete,
                    onError,
                    parentId,
                    parentPath,
                    abortController,
                    set,
                    queryClient,
                  )
                } else {
                  await handleSingleFileUpload(
                    actualFile,
                    uploadingFile,
                    updateProgress,
                    onComplete,
                    onError,
                    parentId,
                    parentPath,
                    abortController,
                    set,
                    queryClient,
                  )
                }
              } catch (e) {
                onError(e as Error)
              }
            })

            await Promise.all(batchPromises)
            console.log(`✅ Batch ${batchIndex + 1}/${batches.length} completed`)

            if (batchIndex < batches.length - 1 && batchDelay > 0) {
              console.log(`⏳ Waiting ${batchDelay}ms before next batch...`)
              await new Promise(resolve => setTimeout(resolve, batchDelay))
            }
          }

          console.log('🎉 All batches completed successfully')
        }

        processFilesInBatches()
      }

      // Add upload task to queue and process queue
      set(state => ({ uploadQueue: [...state.uploadQueue, uploadTask] }))
      get().processUploadQueue()
    },
    handleRetry: async (fileId: number, invalidateQueryKeys?: any[], queryClient?: any) => {
      const { uploadingFiles } = get()
      const fileToRetry = uploadingFiles.find(
        (f: EnhancedUploadingFile) => f.id === fileId,
      ) as EnhancedUploadingFile

      if (!fileToRetry || !fileToRetry.originalFile) {
        console.error('Cannot retry: original file not found')

        return
      }

      // Reset status and progress
      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
          f.id === fileId ? { ...f, status: FileStatus.UPLOADING, progress: 0 } : f,
        ),
      }))

      const updateProgress = (progress: number) => {
        // Only update if progress changed significantly to prevent excessive re-renders
        const currentFile = get().uploadingFiles.find((f: EnhancedUploadingFile) => f.id === fileId)

        if (currentFile && (Math.abs(progress - currentFile.progress) >= 5 || progress === 100)) {
          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
              f.id === fileId ? { ...f, progress } : f,
            ),
          }))
        }
      }

      const updateChunkInfo = (currentChunk: number, totalChunks: number) => {
        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
            f.id === fileId ? { ...f, currentChunk, chunks: totalChunks } : f,
          ),
        }))
      }

      const onComplete = (fileData: any) => {
        const { driveFiles } = get()

        const existingFile = driveFiles.find((f: FileItem) => f.id === parseInt(fileData.id))

        if (!existingFile) {
          const mimeType =
            fileToRetry.originalFile!.type ||
            getMimeTypeFromExtension(fileToRetry.originalFile!.name || 'unknown')
          const newDriveFile: FileItem = {
            id: parseInt(fileData.id),
            name: fileData.name || fileToRetry.originalFile!.name || 'unknown',
            type: mimeType,
            lastModified: new Date(fileData.updatedAt).toLocaleDateString(),
            path: fileData.path,
            createdAt: new Date(fileData.createdAt).toLocaleDateString(),
            mimeType: mimeType,
          }

          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
              return f.id === fileId
                ? {
                    ...f,
                    status: fileData.status || f.status, // Update status from backend response - source of truth
                    progress: 100,
                    name: fileData.name || f.name, // Update the name with the API response
                    originalName: f.originalName || f.name, // Preserve original name if not already set
                    path: fileData.path || f.path, // Update path from backend
                    serverFileId: fileData.id, // Update server file ID
                  }
                : f
            }),
            driveFiles: [...state.driveFiles, newDriveFile],
          }))
          // Check if we're in trash or favorites context - skip cache updates if so
          if (shouldSkipCacheUpdatesWithLog('both', 'folder upload file completion')) {
            return
          }

          // 🚀 IMMEDIATE CACHE UPDATE: Update React Query cache immediately for FileCard
          // This ensures FileCard reflects the status change instantly during retry
          if (fileData.id && queryClient) {
            get().updateFileStatusFromWebSocket(
              fileData.id,
              fileData.status as FileStatus,
              queryClient,
            )
          }
          // Update cache directly instead of invalidating
          const keysToInvalidate = invalidateQueryKeys?.length
            ? invalidateQueryKeys
            : [queryKeys.files.lists()]

          if (queryClient) {
            ;(keysToInvalidate as any[]).forEach(async (key: any) => {
              const queriesToUpdate = queryClient.getQueriesData({ queryKey: key })
              const { insertSorted } = await import('@qse/utils')

              queriesToUpdate.forEach(([queryKey]: [any, any]) => {
                queryClient.setQueryData(queryKey, (oldData: any) => {
                  if (!oldData || !Array.isArray(oldData.data)) {
                    return oldData
                  }

                  const fileExists = oldData.data.some(
                    (f: any) =>
                      f.id === newDriveFile.id ||
                      (f.name === newDriveFile.name && f.path === newDriveFile.path),
                  )

                  if (!fileExists) {
                    return {
                      ...oldData,
                      data: insertSorted(oldData.data, newDriveFile),
                      totalCount: (oldData.totalCount || 0) + 1,
                    }
                  }

                  return {
                    ...oldData,
                    data: oldData.data.map((f: any) =>
                      f.id === newDriveFile.id ||
                      (f.name === newDriveFile.name && f.path === newDriveFile.path)
                        ? { ...f, ...newDriveFile }
                        : f,
                    ),
                  }
                })
              })
            })
          }
        } else {
          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
              f.id === fileId ? { ...f, status: FileStatus.AVAILABLE, progress: 100 } : f,
            ),
          }))
          // Update cache directly instead of invalidating (when file exists)
          const keysToInvalidate = invalidateQueryKeys?.length
            ? invalidateQueryKeys
            : [queryKeys.files.lists()]

          if (queryClient) {
            ;(keysToInvalidate as any[]).forEach((key: any) => {
              const queriesToUpdate = queryClient.getQueriesData({ queryKey: key })

              queriesToUpdate.forEach(([queryKey]: [any, any]) => {
                queryClient.setQueryData(queryKey, (oldData: any) => {
                  if (!oldData || !Array.isArray(oldData.data)) {
                    return oldData
                  }

                  return {
                    ...oldData,
                    data: oldData.data.map((f: any) =>
                      f.id === parseInt(fileData?.id || '0')
                        ? { ...f, status: FileStatus.AVAILABLE }
                        : f,
                    ),
                  }
                })
              })
            })
          }
        }
      }

      const onError = (error: Error) => {
        console.error('Retry failed:', error)
        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
            f.id === fileId ? { ...f, status: FileStatus.FAILED } : f,
          ),
        }))
      }

      try {
        // Retry with the same strategy
        if (fileToRetry.uploadStrategy === 'multipart') {
          await handleMultipartFileUpload(
            fileToRetry.originalFile,
            fileToRetry,
            updateProgress,
            updateChunkInfo,
            onComplete,
            onError,
            fileToRetry.parentId,
            fileToRetry.parentPath,
            undefined,
            undefined,
            queryClient,
          )
        } else {
          await handleSingleFileUpload(
            fileToRetry.originalFile,
            fileToRetry,
            updateProgress,
            onComplete,
            onError,
            fileToRetry.parentId,
            fileToRetry.parentPath,
            undefined,
            undefined,
            queryClient,
          )
        }
      } catch (error) {
        onError(error as Error)
      }
    },

    // [RETRY_FAILED] Retry all retryable failed files in a folder upload
    // Uses individual handleSingleFileUpload/handleMultipartFileUpload per file with parentId
    // so uploads work independently of any folder session state (which may be CANCELLED/COMPLETED).
    handleRetryFailedFiles: async (folderUploadId: string, queryClient?: any) => {
      const { uploadingFiles } = get()
      const folderEntry = uploadingFiles.find(
        (f: EnhancedUploadingFile) => f.isFolderUpload && f.folderUploadId === folderUploadId,
      ) as EnhancedUploadingFile | undefined

      if (!folderEntry || !folderEntry.failedFileDetails?.length) {
        console.error('[RETRY_FAILED] Cannot retry: folder entry or failed files not found')

        return
      }

      // [RETRY_FAILED] Split into retryable vs non-retryable
      const retryableReasons: Array<string> = ['upload', 'zero_bytes']
      const retryableFiles = folderEntry.failedFileDetails.filter(
        (detail: FailedFileDetail) =>
          detail.file && detail.reason && retryableReasons.includes(detail.reason),
      )
      const nonRetryableFiles = folderEntry.failedFileDetails.filter(
        (detail: FailedFileDetail) =>
          !detail.file || !detail.reason || !retryableReasons.includes(detail.reason),
      )

      if (retryableFiles.length === 0) {
        console.warn('[RETRY_FAILED] No retryable files found')

        return
      }

      const totalRetrying = retryableFiles.length

      console.log(
        `[RETRY_FAILED] Retrying ${totalRetrying} failed files for folder ${folderUploadId}`,
      )

      // [RETRY_FAILED] Set folder to UPLOADING with isRetrying tag, reset retryable failure counters
      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          if (f.isFolderUpload && f.folderUploadId === folderUploadId) {
            return {
              ...f,
              status: FileStatus.UPLOADING,
              isRetrying: true,
              failedFiles: nonRetryableFiles.length,
              failedUploads: nonRetryableFiles.filter(d => d.reason === 'upload').length,
              failedEncryptions: nonRetryableFiles.filter(d => d.reason === 'encryption').length,
              failedFileDetails: nonRetryableFiles,
            }
          }

          return f
        }),
      }))

      let retrySuccessCount = 0
      let retryFailCount = 0

      // [RETRY_FAILED] Process each file individually via handleSingleFileUpload / handleMultipartFileUpload
      const uploadPromises = retryableFiles.map(async (detail: FailedFileDetail) => {
        const file = detail.file!
        const parentId = detail.parentId || folderUploadId

        // Create a minimal EnhancedUploadingFile for the upload handlers
        const mockUploadingFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          serverFileId: undefined,
        } as EnhancedUploadingFile

        const updateProgress = () => {} // Progress tracked at folder level
        const updateChunkInfo = () => {}

        const onComplete = () => {
          retrySuccessCount++
          console.log(
            `[RETRY_FAILED] File "${file.name}" uploaded successfully (${retrySuccessCount}/${totalRetrying})`,
          )

          // [RETRY_FAILED] Update folder uploaded count
          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
              if (f.isFolderUpload && f.folderUploadId === folderUploadId) {
                return { ...f, uploadedFiles: (f.uploadedFiles || 0) + 1 }
              }

              return f
            }),
          }))
        }

        const onError = (error: Error) => {
          retryFailCount++
          console.error(
            `[RETRY_FAILED] Retry failed for "${file.name}" (${retryFailCount}/${totalRetrying}):`,
            error,
          )

          const reFailedDetail: FailedFileDetail = {
            name: detail.name,
            path: detail.path,
            reason: detail.reason,
            file: detail.file,
            parentId: detail.parentId,
          }

          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
              if (f.isFolderUpload && f.folderUploadId === folderUploadId) {
                return {
                  ...f,
                  failedFiles: (f.failedFiles || 0) + 1,
                  failedUploads: (f.failedUploads || 0) + 1,
                  failedFileDetails: addOrUpdateFailedFileDetail(
                    f.failedFileDetails,
                    reFailedDetail,
                  ),
                }
              }

              return f
            }),
          }))
        }

        try {
          if (file.size >= MULTIPART_THRESHOLD) {
            await handleMultipartFileUpload(
              file,
              mockUploadingFile,
              updateProgress,
              updateChunkInfo,
              onComplete,
              onError,
              parentId,
              detail.path,
              undefined,
              undefined,
              queryClient,
            )
          } else {
            await handleSingleFileUpload(
              file,
              mockUploadingFile,
              updateProgress,
              onComplete,
              onError,
              parentId,
              detail.path,
              undefined,
              undefined,
              queryClient,
            )
          }
        } catch (error) {
          onError(error as Error)
        }
      })

      await Promise.allSettled(uploadPromises)

      // [RETRY_FAILED] Final status update: set AVAILABLE, clear isRetrying
      const currentFolder = get().uploadingFiles.find(
        (f: EnhancedUploadingFile) => f.isFolderUpload && f.folderUploadId === folderUploadId,
      ) as EnhancedUploadingFile | undefined
      const remainingFailures = currentFolder?.failedFileDetails?.length || 0

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          if (f.isFolderUpload && f.folderUploadId === folderUploadId) {
            return {
              ...f,
              status: FileStatus.AVAILABLE,
              isRetrying: false,
            }
          }

          return f
        }),
      }))

      console.log(
        `[RETRY_FAILED] Retry complete for folder ${folderUploadId}: ${retrySuccessCount} succeeded, ${retryFailCount} failed, ${remainingFailures} total remaining failures`,
      )
    },

    handleAbort: async (fileId: number, queryClient?: any) => {
      const { abortControllers, uploadingFiles } = get()
      const controller = abortControllers.get(fileId)
      const uploadingFile = uploadingFiles.find(
        (f: EnhancedUploadingFile) => f.id === fileId,
      ) as EnhancedUploadingFile

      if (!uploadingFile) {
        console.warn(`File with id ${fileId} not found`)

        return
      }

      // Step 1: Abort S3 upload XHR immediately (this stops S3 upload but NOT start request)
      if (controller) {
        controller.abort()
        console.log(`AbortController aborted for fileId: ${fileId} (stops S3 upload)`)
      }

      // Step 2: Mark as "cancelling" for ALL files (wait for serverFileId OR abort API completion)
      const hasServerId = !!(
        uploadingFile.serverFileId ||
        (uploadingFile.isFolderUpload && uploadingFile.folderUploadId)
      )

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
          f.id === fileId
            ? {
                ...f,
                isCancelling: true, // Always show "Cancelling..." during the cancel process
                errorMessage: 'Cancelling upload...',
              }
            : f,
        ),
      }))

      // Step 3: Wait for serverFileId/folderUploadId if we don't have it yet
      if (!hasServerId) {
        const isFolder = uploadingFile.isFolderUpload
        const idType = isFolder ? 'folderUploadId' : 'serverFileId'

        console.log(
          `${isFolder ? 'Folder' : 'File'} ${uploadingFile.name} has no ${idType} yet - waiting for ${isFolder ? 'initiate' : 'start'} response...`,
        )

        // Poll for serverFileId/folderUploadId with timeout (max 10 seconds)
        const maxWaitTime = 10000
        const pollInterval = 100
        const startTime = Date.now()
        let idReceived = false

        while (Date.now() - startTime < maxWaitTime) {
          const currentFile = get().uploadingFiles.find(
            (f: EnhancedUploadingFile) => f.id === fileId,
          )

          if (currentFile) {
            if (currentFile.isFolderUpload && (currentFile as any).folderUploadId) {
              console.log(
                `✅ Got folderUploadId for ${uploadingFile.name}: ${(currentFile as any).folderUploadId}`,
              )
              uploadingFile.folderUploadId = (currentFile as any).folderUploadId
              idReceived = true

              break
            } else if (!currentFile.isFolderUpload && currentFile.serverFileId) {
              console.log(
                `✅ Got serverFileId for ${uploadingFile.name}: ${currentFile.serverFileId}`,
              )
              uploadingFile.serverFileId = currentFile.serverFileId
              idReceived = true

              break
            }
          }

          await new Promise(resolve => setTimeout(resolve, pollInterval))
        }

        // If still no ID after timeout, upload was cancelled before reaching server
        if (!idReceived) {
          console.log(
            `⏱️ Timeout waiting for ${idType} for ${uploadingFile.name} - cancelled before server response`,
          )

          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
              f.id === fileId
                ? {
                    ...f,
                    status: FileStatus.FAILED,
                    errorMessage: 'Upload cancelled',
                    isCancelling: false,
                  }
                : f,
            ),
          }))

          return
        }
      }

      // Step 4: Now we have serverFileId - call backend to clean up
      // Keep isCancelling: true during API call

      // Handle folder upload cancellation
      if (uploadingFile.isFolderUpload && uploadingFile.folderUploadId) {
        try {
          console.log(
            `Making API call to cancel folder upload: ${uploadingFile.name} with folder ID: ${uploadingFile.folderUploadId}`,
          )
          const { cancelFolderUpload } = await import('@qse/api')

          await cancelFolderUpload(uploadingFile.folderUploadId)
          console.log(`Successfully cancelled folder upload for: ${uploadingFile.name}`)

          // Abort the parent folder controller to cascade to all individual file controllers
          const { folderAbortControllers } = get()
          const folderController = folderAbortControllers.get(uploadingFile.folderUploadId)

          if (folderController) {
            console.log(`Aborting parent folder controller for: ${uploadingFile.name}`)
            folderController.abort()
            folderAbortControllers.delete(uploadingFile.folderUploadId)
          }
        } catch (error: any) {
          console.error(`Failed to cancel folder upload for: ${uploadingFile.name}`, error)
        } finally {
          removeUploadFromStorage(uploadingFile.folderUploadId, 'folder')
        }
      }
      // Handle individual file upload cancellation
      else if (uploadingFile.serverFileId) {
        try {
          console.log(
            `Making API call to abort upload for file: ${uploadingFile.name} with server ID: ${uploadingFile.serverFileId}`,
          )
          await abortFileUpload(uploadingFile.serverFileId)
          console.log(`Successfully aborted upload for file: ${uploadingFile.name}`)
        } catch (error: any) {
          console.error(`Failed to abort upload for file: ${uploadingFile.name}`, error)
        } finally {
          removeUploadFromStorage(uploadingFile.serverFileId, 'file')
        }
      }

      // Step 5: Final state update after backend cleanup - mark as cancelled
      console.log(
        `✅ Marking ${uploadingFile.isFolderUpload ? 'folder' : 'file'} ${uploadingFile.name} as cancelled`,
      )

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
          f.id === fileId
            ? {
                ...f,
                status: FileStatus.FAILED,
                errorMessage: 'Upload cancelled',
                isCancelling: false,
              }
            : f,
        ),
      }))

      // Handle cache updates differently for folders vs files
      if (uploadingFile.isFolderUpload) {
        // For folders: Keep in file explorer but mark as AVAILABLE (completed)
        // This preserves the folder structure that was created
        if (uploadingFile.folderUploadId && queryClient) {
          const { queryKeys } = await import('@qse/api')

          const updateFolderStatus = (oldData: any) => {
            if (!oldData) return oldData

            if (oldData.pages) {
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) => ({
                  ...page,
                  data: (page.data || []).map((f: any) =>
                    f.id === uploadingFile.folderUploadId
                      ? { ...f, status: FileStatus.AVAILABLE }
                      : f,
                  ),
                })),
              }
            }

            if (oldData.data) {
              return {
                ...oldData,
                data: (oldData.data || []).map((f: any) =>
                  f.id === uploadingFile.folderUploadId
                    ? { ...f, status: FileStatus.AVAILABLE }
                    : f,
                ),
              }
            }

            return oldData
          }

          const allFileQueries = queryClient.getQueriesData({
            queryKey: queryKeys.files.lists(),
          })

          allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
            if (!cacheData) return

            const queryFilter = queryKey[2] || {}
            const queryParentId = queryFilter.parentId
            const folderParentId = uploadingFile.parentId

            const shouldContainFolder =
              queryParentId === folderParentId ||
              (queryParentId === null &&
                (folderParentId === '' ||
                  folderParentId === null ||
                  folderParentId === undefined)) ||
              (queryParentId === '' && (folderParentId === null || folderParentId === undefined)) ||
              (queryParentId === undefined &&
                (folderParentId === null || folderParentId === '' || folderParentId === undefined))

            if (shouldContainFolder) {
              queryClient.setQueryData(queryKey, (oldData: any) => updateFolderStatus(oldData))
            }
          })
        }

        toast.info('Folder upload cancelled')
      } else {
        // For individual files: Remove from file explorer
        if (uploadingFile.serverFileId && queryClient) {
          const fileParentId = uploadingFile.parentId
          const { queryKeys } = await import('@qse/api')

          const removeFileFromData = (oldData: any) => {
            if (!oldData) return oldData

            if (oldData.pages) {
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) => ({
                  ...page,
                  data: (page.data || []).filter((f: any) => f.id !== uploadingFile.serverFileId),
                  totalCount: Math.max(0, (page.totalCount || 0) - 1),
                })),
              }
            }

            if (oldData.data) {
              return {
                ...oldData,
                data: (oldData.data || []).filter((f: any) => f.id !== uploadingFile.serverFileId),
                totalCount: Math.max(0, (oldData.totalCount || 0) - 1),
              }
            }

            return oldData
          }

          const allFileQueries = queryClient.getQueriesData({
            queryKey: queryKeys.files.lists(),
          })

          allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
            if (!cacheData) return

            const queryFilter = queryKey[2] || {}
            const queryParentId = queryFilter.parentId

            const shouldContainFile =
              queryParentId === fileParentId ||
              (queryParentId === null &&
                (fileParentId === '' || fileParentId === null || fileParentId === undefined)) ||
              (queryParentId === '' && (fileParentId === null || fileParentId === undefined)) ||
              (queryParentId === undefined &&
                (fileParentId === null || fileParentId === '' || fileParentId === undefined))

            if (shouldContainFile) {
              queryClient.setQueryData(queryKey, (oldData: any) => removeFileFromData(oldData))
            }
          })
        }

        toast.info('Upload cancelled')
      }

      // Clean up abort controller
      if (controller) {
        const newControllers = new Map(abortControllers)

        newControllers.delete(fileId)
        set({ abortControllers: newControllers })
      }

      // Only remove beforeunload listener if all uploads are complete/failed
      const remainingUploads = get().uploadingFiles.filter(
        (f: EnhancedUploadingFile) => f.status === FileStatus.UPLOADING,
      )

      if (remainingUploads.length <= 1) {
        get().removeBeforeUnloadListener()
      }
    },
    handleAbortAll: async (queryClient?: any) => {
      const { abortControllers, uploadingFiles } = get()

      console.log(`handleAbortAll called`)
      console.log(`Total uploading files:`, uploadingFiles.length)
      console.log(`AbortControllers count:`, abortControllers.size)

      // Count files that are being aborted (only UPLOADING files, not ENCRYPTING)
      const filesToAbort = uploadingFiles.filter(
        (f: EnhancedUploadingFile) => f.status === FileStatus.UPLOADING,
      )
      const foldersToAbort = filesToAbort.filter(f => f.isFolderUpload).length
      const individualFilesToAbort = filesToAbort.length - foldersToAbort

      // Set the abort flag to stop sequential processing AND mark all files as cancelling
      set((state: GlobalFileUploadState) => ({
        ...state,
        isAborted: true,
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
          f.status === FileStatus.UPLOADING
            ? { ...f, isCancelling: true, errorMessage: 'Cancelling upload...' }
            : f,
        ),
      }))

      // Handle folder uploads first - use fresh state after marking as cancelling
      const folderUploads = get().uploadingFiles.filter(
        (f: EnhancedUploadingFile) =>
          (f as any).isFolderUpload && f.status === FileStatus.UPLOADING,
      ) as EnhancedUploadingFile[]

      if (folderUploads.length > 0) {
        try {
          // Import the cancelFolderUpload function
          const { cancelFolderUpload } = await import('@qse/api')

          // Cancel all folder uploads that have been registered with backend
          const folderCancelPromises = folderUploads
            .filter(folderUpload => (folderUpload as any).folderUploadId)
            .map(async folderUpload => {
              const folderId = (folderUpload as any).folderUploadId

              try {
                console.log(`Cancelling folder upload for folderId: ${folderId}`)
                await cancelFolderUpload(folderId)
                console.log(`Successfully cancelled folder upload for folderId: ${folderId}`)
              } catch (error) {
                console.error(`Failed to cancel folder upload for folderId: ${folderId}`, error)
              } finally {
                removeUploadFromStorage(folderId, 'folder')
              }
            })

          // Wait for all folder cancellations to complete
          await Promise.all(folderCancelPromises)
          console.log('All folder upload cancellations completed')

          // Log folders that were cancelled before backend registration
          const foldersWithoutId = folderUploads.filter(f => !(f as any).folderUploadId)

          if (foldersWithoutId.length > 0) {
            console.log(
              `${foldersWithoutId.length} folder(s) were cancelled before backend registration - no API cancel needed`,
            )
          }
        } catch (error: any) {
          console.error(`Failed to cancel folder uploads`, error)
        }
      }

      // CRITICAL: Abort ALL active upload controllers immediately after cancel API calls
      // This stops all ongoing uploads (both individual files and folder uploads)
      console.log('Aborting all active upload controllers...')
      const allActiveControllers = Array.from(abortControllers.entries())
      const { folderAbortControllers } = get()
      const allFolderControllers = Array.from(folderAbortControllers.entries())

      console.log('allActiveControllers', allActiveControllers)
      console.log('allFolderControllers', allFolderControllers)

      const abortPromises = allActiveControllers.map(([fileId, controller]) => {
        return new Promise<void>(resolve => {
          if (controller) {
            // Listen for the abort signal to complete
            controller.signal.addEventListener('abort', () => {
              console.log(`AbortController completed for fileId: ${fileId}`)
              resolve()
            })

            // Trigger the abort
            controller.abort()
            abortControllers.delete(fileId)
          } else {
            // No controller found, resolve immediately
            resolve()
          }
        })
      })

      // Add folder abort controller handling
      const folderAbortPromises = allFolderControllers.map(([folderId, controller]) => {
        return new Promise<void>(resolve => {
          if (controller) {
            // Listen for the abort signal to complete
            controller.signal.addEventListener('abort', () => {
              console.log(`Folder AbortController completed for folderId: ${folderId}`)
              resolve()
            })

            // Trigger the abort - this will cascade to all individual file controllers
            controller.abort()
            folderAbortControllers.delete(folderId)
          } else {
            // No controller found, resolve immediately
            resolve()
          }
        })
      })

      // Wait for all abort operations to complete
      await Promise.all([...abortPromises, ...folderAbortPromises])
      console.log('All upload controllers aborted')

      // Handle individual file uploads API cancellation (separate from controller abort)
      // Separate files into two groups: those with serverFileId and those without
      // Use fresh state to ensure we have the latest file information
      const currentUploadingFiles = get().uploadingFiles
      const filesWithServerId = currentUploadingFiles.filter(
        (f: EnhancedUploadingFile) =>
          f.status === FileStatus.UPLOADING && !(f as any).isFolderUpload && f.serverFileId,
      ) as EnhancedUploadingFile[]

      const filesWithoutServerId = currentUploadingFiles.filter(
        (f: EnhancedUploadingFile) =>
          f.status === FileStatus.UPLOADING && !(f as any).isFolderUpload && !f.serverFileId,
      ) as EnhancedUploadingFile[]

      // Wait for files without serverFileId to get their IDs
      if (filesWithoutServerId.length > 0) {
        console.log(
          `${filesWithoutServerId.length} file(s) don't have serverFileId yet - waiting for start response...`,
        )

        // Wait for all serverFileIds to arrive (with timeout)
        const maxWaitTime = 10000
        const startTime = Date.now()

        while (Date.now() - startTime < maxWaitTime && filesWithoutServerId.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 200))

          // Check which files got serverFileIds
          const currentFiles = get().uploadingFiles

          for (let i = filesWithoutServerId.length - 1; i >= 0; i--) {
            const file = filesWithoutServerId[i]
            const currentFile = currentFiles.find((f: EnhancedUploadingFile) => f.id === file.id)

            if (currentFile?.serverFileId) {
              console.log(`✅ Got serverFileId for ${file.name}: ${currentFile.serverFileId}`)
              file.serverFileId = currentFile.serverFileId
              filesWithServerId.push(file)
              filesWithoutServerId.splice(i, 1)
            }
          }

          if (filesWithoutServerId.length === 0) break
        }

        if (filesWithoutServerId.length > 0) {
          console.log(
            `⏱️ Timeout waiting for ${filesWithoutServerId.length} file(s) - they were cancelled before server response`,
          )
        }
      }

      // Abort files that have been registered with backend
      if (filesWithServerId.length > 0) {
        const fileIds = filesWithServerId
          .map(f => f.serverFileId)
          .filter((id): id is string => Boolean(id))

        try {
          await bulkAbortUploads({
            fileIds,
          })
        } catch (error: any) {
          console.error(`Failed to abort individual file uploads via API`, error)
          // Don't throw - continue with UI update
        } finally {
          fileIds.forEach(id => removeUploadFromStorage(id, 'file'))
        }
      }

      console.log(
        `Total files cancelled: ${filesWithServerId.length + filesWithoutServerId.length} (${filesWithServerId.length} with backend API, ${filesWithoutServerId.length} before registration)`,
      )

      // Clear all controllers and mark all uploading files as aborted
      console.log(`✅ Marking all files as cancelled (clearing all isCancelling flags)`)

      try {
        set((state: GlobalFileUploadState) => ({
          abortControllers: new Map<number, AbortController>(),
          folderAbortControllers: new Map<string, AbortController>(),
          uploadingFiles: state.uploadingFiles
            .map((f: EnhancedUploadingFile) => {
              // Clear isCancelling for ANY file that has it set
              if (f.isCancelling) {
                return {
                  ...f,
                  status: FileStatus.FAILED,
                  errorMessage: 'Upload cancelled',
                  isCancelling: false,
                }
              }
              // Also mark any remaining UPLOADING files as cancelled (not ENCRYPTING)
              if (f.status === FileStatus.UPLOADING) {
                return {
                  ...f,
                  status: FileStatus.FAILED,
                  errorMessage: 'Upload cancelled',
                  isCancelling: false,
                }
              }

              return f
            })
            // Remove hidden individual files from cancelled folder uploads
            .filter((f: EnhancedUploadingFile) => !f.isHidden),
          // Keep the upload modal open to show cancellation status
          // isUploadModalOpen: false, // Removed - keep tracker visible
        }))

        // Show success toast for cancellation (use counts from before abort)
        if (filesToAbort.length > 0) {
          if (foldersToAbort > 0 && individualFilesToAbort > 0) {
            toast.info(
              `All uploads cancelled (${foldersToAbort} folder${foldersToAbort > 1 ? 's' : ''}, ${individualFilesToAbort} file${individualFilesToAbort > 1 ? 's' : ''})`,
            )
          } else if (foldersToAbort > 0) {
            toast.info(`${foldersToAbort} folder upload${foldersToAbort > 1 ? 's' : ''} cancelled`)
          } else if (individualFilesToAbort > 0) {
            toast.info(
              `${individualFilesToAbort} upload${individualFilesToAbort > 1 ? 's' : ''} cancelled`,
            )
          }
        }

        // Don't automatically remove failed files - let the user manually close the tracker
        // This ensures the tracker stays visible to show the cancellation status
        // The user can manually close the tracker when they're done viewing the status
      } catch (error) {
        console.error('Failed to update state after abort:', error)
      }

      // Remove beforeunload listener since all uploads are aborted
      get().removeBeforeUnloadListener()

      // Update cache to reflect cancelled uploads
      if (queryClient) {
        try {
          const { queryKeys } = await import('@qse/api')

          // Separate cancelled folders and individual files
          const cancelledFolders = get().uploadingFiles.filter(
            (f: EnhancedUploadingFile) =>
              f.errorMessage === 'Upload cancelled' && f.isFolderUpload && f.folderUploadId,
          )

          const cancelledIndividualFiles = get().uploadingFiles.filter(
            (f: EnhancedUploadingFile) =>
              f.errorMessage === 'Upload cancelled' && !f.isFolderUpload && f.serverFileId,
          )

          const allFileQueries = queryClient.getQueriesData({
            queryKey: queryKeys.files.lists(),
          })

          allFileQueries.forEach(([queryKey]: [any, any]) => {
            queryClient.setQueryData(queryKey, (oldData: any) => {
              if (!oldData) return oldData

              let nextData = oldData

              if (cancelledFolders.length > 0) {
                nextData = (() => {
                  if (nextData.pages) {
                    return {
                      ...nextData,
                      pages: nextData.pages.map((page: any) => ({
                        ...page,
                        data: (page.data || []).map((f: any) => {
                          const folder = cancelledFolders.find(
                            (cf: EnhancedUploadingFile) => cf.folderUploadId === f.id,
                          )

                          return folder ? { ...f, status: FileStatus.AVAILABLE } : f
                        }),
                      })),
                    }
                  }

                  if (nextData.data) {
                    return {
                      ...nextData,
                      data: (nextData.data || []).map((f: any) => {
                        const folder = cancelledFolders.find(
                          (cf: EnhancedUploadingFile) => cf.folderUploadId === f.id,
                        )

                        return folder ? { ...f, status: FileStatus.AVAILABLE } : f
                      }),
                    }
                  }

                  return nextData
                })()
              }

              if (cancelledIndividualFiles.length > 0) {
                const cancelledFileIds = cancelledIndividualFiles.map(
                  (f: EnhancedUploadingFile) => f.serverFileId,
                )

                nextData = (() => {
                  if (nextData.pages) {
                    return {
                      ...nextData,
                      pages: nextData.pages.map((page: any) => ({
                        ...page,
                        data: (page.data || []).filter(
                          (f: any) => !cancelledFileIds.includes(f.id),
                        ),
                        totalCount: Math.max(
                          0,
                          (page.totalCount || 0) -
                            (page.data || []).filter((f: any) => cancelledFileIds.includes(f.id))
                              .length,
                        ),
                      })),
                    }
                  }

                  if (nextData.data) {
                    const removedCount = (nextData.data || []).filter((f: any) =>
                      cancelledFileIds.includes(f.id),
                    ).length

                    return {
                      ...nextData,
                      data: (nextData.data || []).filter(
                        (f: any) => !cancelledFileIds.includes(f.id),
                      ),
                      totalCount: Math.max(0, (nextData.totalCount || 0) - removedCount),
                    }
                  }

                  return nextData
                })()
              }

              return nextData
            })
          })

          console.log('Successfully updated cache after abort')
        } catch (error) {
          console.error('Failed to update cache after abort:', error)
        }
      }
    },
    handleLogoutAbort: async (_queryClient?: any) => {
      const { abortControllers, uploadingFiles, folderAbortControllers } = get()

      // Set the abort flag and mark all files with logout-specific message
      set((state: GlobalFileUploadState) => ({
        ...state,
        isAborted: true,
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
          f.status === FileStatus.UPLOADING
            ? {
                ...f,
                isCancelling: true,
                errorMessage: "You've been signed out. Upload paused.",
              }
            : f,
        ),
      }))

      // Handle folder uploads first
      const folderUploads = uploadingFiles.filter(
        (f: EnhancedUploadingFile) =>
          (f as any).isFolderUpload && f.status === FileStatus.UPLOADING,
      ) as EnhancedUploadingFile[]

      if (folderUploads.length > 0) {
        try {
          const { cancelFolderUpload } = await import('@qse/api')

          const folderCancelPromises = folderUploads
            .filter(folderUpload => (folderUpload as any).folderUploadId)
            .map(async folderUpload => {
              const folderId = (folderUpload as any).folderUploadId

              try {
                await cancelFolderUpload(folderId)
              } catch (error) {
                // Silently fail on 401 errors during logout - expected behavior
                if ((error as any)?.response?.status !== 401) {
                  console.error('Failed to cancel folder upload during logout:', error)
                }
              } finally {
                removeUploadFromStorage(folderId, 'folder')
              }
            })

          await Promise.all(folderCancelPromises)
        } catch (error: any) {
          console.error('Failed to cancel folder uploads during logout:', error)
        }
      }

      // Abort all active controllers
      const allActiveControllers = Array.from(abortControllers.entries())
      const allFolderControllers = Array.from(folderAbortControllers.entries())

      const abortPromises = allActiveControllers.map(([fileId, controller]) => {
        return new Promise<void>(resolve => {
          if (controller) {
            controller.signal.addEventListener('abort', () => resolve())
            controller.abort()
            abortControllers.delete(fileId)
          } else {
            resolve()
          }
        })
      })

      const folderAbortPromises = allFolderControllers.map(([folderId, controller]) => {
        return new Promise<void>(resolve => {
          if (controller) {
            controller.signal.addEventListener('abort', () => resolve())
            controller.abort()
            folderAbortControllers.delete(folderId)
          } else {
            resolve()
          }
        })
      })

      await Promise.all([...abortPromises, ...folderAbortPromises])

      // Handle individual file uploads API cancellation
      const filesWithServerId = uploadingFiles.filter(
        (f: EnhancedUploadingFile) =>
          f.status === FileStatus.UPLOADING && !(f as any).isFolderUpload && f.serverFileId,
      ) as EnhancedUploadingFile[]

      const filesWithoutServerId = uploadingFiles.filter(
        (f: EnhancedUploadingFile) =>
          f.status === FileStatus.UPLOADING && !(f as any).isFolderUpload && !f.serverFileId,
      ) as EnhancedUploadingFile[]

      // Wait briefly for files without serverFileId (but with shorter timeout since we're logging out)
      if (filesWithoutServerId.length > 0) {
        const maxWaitTime = 3000 // Only wait 3 seconds during logout
        const startTime = Date.now()

        while (Date.now() - startTime < maxWaitTime && filesWithoutServerId.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 200))

          const currentFiles = get().uploadingFiles

          for (let i = filesWithoutServerId.length - 1; i >= 0; i--) {
            const file = filesWithoutServerId[i]
            const currentFile = currentFiles.find((f: EnhancedUploadingFile) => f.id === file.id)

            if (currentFile?.serverFileId) {
              file.serverFileId = currentFile.serverFileId
              filesWithServerId.push(file)
              filesWithoutServerId.splice(i, 1)
            }
          }

          if (filesWithoutServerId.length === 0) break
        }
      }

      // Abort files that have been registered with backend (ignore 401 errors)
      if (filesWithServerId.length > 0) {
        try {
          const fileIds = filesWithServerId.map(f => f.serverFileId as string)

          await bulkAbortUploads({ fileIds })
        } catch (error: any) {
          // Silently fail on 401 errors during logout - expected behavior
          if (error?.response?.status !== 401) {
            console.error('Failed to abort file uploads during logout:', error)
          }
        } finally {
          filesWithServerId.forEach(file => {
            if (file.serverFileId) {
              removeUploadFromStorage(file.serverFileId, 'file')
            }
          })
        }
      }

      // Mark all files with logout-specific message
      try {
        set((state: GlobalFileUploadState) => ({
          abortControllers: new Map<number, AbortController>(),
          folderAbortControllers: new Map<string, AbortController>(),
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
            if (
              f.isCancelling ||
              f.status === FileStatus.UPLOADING ||
              f.status === FileStatus.ENCRYPTING
            ) {
              return {
                ...f,
                status: FileStatus.FAILED,
                errorMessage: "You've been signed out. Upload paused. Please sign in to continue.",
                isCancelling: false,
              }
            }

            return f
          }),
        }))
      } catch (error) {
        console.error('Failed to update state after logout abort:', error)
      }

      // Remove beforeunload listener since we're logging out
      get().removeBeforeUnloadListener()
    },
    setupBeforeUnloadListener: () => {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        const { uploadingFiles } = get()
        const hasUploadingFiles = uploadingFiles.some(
          (f: EnhancedUploadingFile) => f.status === FileStatus.UPLOADING,
        )

        if (hasUploadingFiles) {
          // Save current state before leaving
          saveUploadingFilesToStorage(uploadingFiles)
          event.preventDefault()
          event.returnValue =
            'All in-flight uploads will be cancelled. Are you sure you want to continue?'

          return 'All in-flight uploads will be cancelled. Are you sure you want to continue?'
        }
      }

      // Remove any existing listener first
      const { beforeUnloadHandler } = get()

      if (beforeUnloadHandler) {
        window.removeEventListener('beforeunload', beforeUnloadHandler)
      }

      // Add new listener
      window.addEventListener('beforeunload', handleBeforeUnload)

      set((state: GlobalFileUploadState) => ({
        ...state,
        beforeUnloadHandler: handleBeforeUnload,
      }))
    },
    removeBeforeUnloadListener: () => {
      const { beforeUnloadHandler } = get()

      if (beforeUnloadHandler) {
        window.removeEventListener('beforeunload', beforeUnloadHandler)
      }

      set((state: GlobalFileUploadState) => ({
        ...state,
        beforeUnloadHandler: null,
      }))
    },
    updateFileStatusFromWebSocket: (fileId: string, status: FileStatus, queryClient?: any) => {
      const currentState = get()
      const file = currentState.uploadingFiles.find(f => f.serverFileId === fileId) as
        | EnhancedUploadingFile
        | undefined

      // Helper function to update file status in all relevant queries
      const updateFileStatusInCache = (status: FileStatus) => {
        if (!queryClient || !file) return

        const fileParentId = file.parentId

        const updateStatusInData = (oldData: any) => {
          if (!oldData) return oldData

          if (oldData.pages) {
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) => ({
                ...page,
                data: (page.data || []).map((f: any) =>
                  f.id === fileId ? { ...f, status: status } : f,
                ),
              })),
            }
          }

          if (oldData.data) {
            return {
              ...oldData,
              data: (oldData.data || []).map((f: any) =>
                f.id === fileId ? { ...f, status: status } : f,
              ),
            }
          }

          return oldData
        }

        // Get ALL file list queries (with various filters)
        const allFileQueries = queryClient.getQueriesData({
          queryKey: queryKeys.files.lists(), // ['files', 'list']
        })

        // Update only queries that should contain this file (matching parentId)
        allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
          if (!cacheData) return

          // Extract filter from query key: ['files', 'list', { parentId: 'xyz', ... }]
          const queryFilter = queryKey[2] || {}
          const queryParentId = queryFilter.parentId

          // Check if this query should contain the file
          // Match when: query parentId === file parentId (both null, both undefined, or same ID)
          const shouldContainFile =
            queryParentId === fileParentId ||
            (queryParentId === null && fileParentId === '') ||
            (queryParentId === '' && fileParentId === null) ||
            (queryParentId === undefined && fileParentId === null) ||
            (queryParentId === null && fileParentId === undefined)

          if (shouldContainFile) {
            queryClient.setQueryData(queryKey, (oldData: any) => updateStatusInData(oldData))
          }
        })
      }

      if (file && file.status === FileStatus.ENCRYPTING && status === FileStatus.AVAILABLE) {
        // If changing from ENCRYPTING to AVAILABLE, add a minimum delay
        const timeSinceEncryptionStart = Date.now() - (file.encryptionStartTime || Date.now())
        const minimumDuration = 3000 // 3 seconds

        if (timeSinceEncryptionStart < minimumDuration) {
          const remainingTime = minimumDuration - timeSinceEncryptionStart

          // Keep showing ENCRYPTING status in cache for minimum duration
          // This overrides the AVAILABLE status that might have been refetched
          updateFileStatusInCache(FileStatus.ENCRYPTING)

          setTimeout(() => {
            set((state: GlobalFileUploadState) => ({
              uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
                f.serverFileId === fileId ? { ...f, status: status } : f,
              ),
            }))

            // Also update ghost file in cache after minimum duration
            updateFileStatusInCache(status)
          }, remainingTime)

          return
        }
      }

      // Set encryption start time when status changes to ENCRYPTING
      if (status === FileStatus.ENCRYPTING) {
        // Don't override files that have already been marked as FAILED
        if (file && file.status === FileStatus.FAILED) {
          return
        }

        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
            f.serverFileId === fileId
              ? { ...f, status: status, encryptionStartTime: Date.now() }
              : f,
          ),
        }))

        // Update ghost file in cache to show ENCRYPTING status
        updateFileStatusInCache(status)

        return
      }

      // Immediate status update for other cases
      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) =>
          f.serverFileId === fileId ? { ...f, status: status } : f,
        ),
      }))

      // Update ghost file in cache for immediate status changes
      updateFileStatusInCache(status)
    },
    updateFileEncryptionProgress: (
      fileId: string,
      progress: {
        percentage: number
        bytesProcessed: number
        totalBytes: number
        throughput: number
        estimatedTimeRemaining: number
      },
    ) => {
      const currentTime = Date.now()

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          // Find file by serverFileId
          if (f.serverFileId === fileId) {
            // Only update if file is in ENCRYPTING status
            if (f.status !== FileStatus.ENCRYPTING && (f.status as string) !== 'ENCRYPTING') {
              return f
            }

            // Prevent updating with smaller progress (handle out-of-order events)
            // If we already have a progress value and the new one is smaller, keep the current one
            const currentProgress = f.encryptionProgress || 0

            if (progress.percentage < currentProgress) return f

            return {
              ...f,
              encryptionProgress: progress.percentage,
              encryptionBytesProcessed: progress.bytesProcessed,
              encryptionTotalBytes: progress.totalBytes,
              encryptionThroughput: progress.throughput,
              encryptionEta: progress.estimatedTimeRemaining,
              lastEncryptionUpdate: currentTime,
            }
          }

          return f
        }),
      }))
    },
    updateFileEncryptionFailureForFolder: (
      fileId: string,
      fileName: string,
      filePath: string,
      parentId?: string,
    ) => {
      console.log('🔒 [EncryptionFailure] Tracking encryption failure for folder upload:', {
        fileId,
        fileName,
        filePath,
        parentId,
      })

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          // Check if this is a folder upload entry
          if (f.isFolderUpload && f.folderUploadId) {
            // Check if the failed file belongs to this folder upload
            // We can determine this by checking if the file's parentId matches this folder's upload ID
            // or if the file's parentId is a subfolder of this folder upload
            const isFileInThisFolder =
              parentId === f.folderUploadId || // Direct child of the main folder
              (parentId && f.pathToId && Array.from(f.pathToId.values()).includes(parentId)) // Child of a subfolder

            if (isFileInThisFolder) {
              console.log('🔒 [EncryptionFailure] File belongs to folder upload:', {
                folderUploadId: f.folderUploadId,
                folderName: f.name,
                failedFile: fileName,
              })

              // Create failed file detail for encryption failure
              const failedFileDetail: FailedFileDetail = {
                name: fileName,
                path: filePath,
                reason: 'encryption',
              }

              return {
                ...f,
                failedFileDetails: addOrUpdateFailedFileDetail(
                  f.failedFileDetails,
                  failedFileDetail,
                ),
              }
            }
          }

          return f
        }),
      }))
    },
    updateFolderUploadProgressFromWebSocket: (
      payload: FolderUploadProgressPayload,
      _queryClient?: any,
    ) => {
      // Get current folder state before update to detect status transition
      const currentFolder = get().uploadingFiles.find(
        f => f.isFolderUpload && f.folderUploadId === payload.folderId,
      )

      // If folder upload not found in tracker, it might have been removed - ignore updates
      if (!currentFolder) {
        console.log('📊 [FOLDER_UPLOAD_PROGRESS] Folder not found in tracker, ignoring update:', {
          folderId: payload.folderId,
        })

        return
      }

      const previousStatus = currentFolder?.status

      // Check if payload indicates folder was deleted/moved (destination no longer exists)
      const payloadStatus = payload.status?.toUpperCase()
      const isFolderDeleted =
        payloadStatus === 'FAILED' ||
        payloadStatus === 'ERROR' ||
        payloadStatus === 'NOT_FOUND' ||
        payload.progress?.upload?.failed === payload.totalFiles ||
        (payload as any).error?.toLowerCase().includes('not found') ||
        (payload as any).error?.toLowerCase().includes('deleted') ||
        (payload as any).error?.toLowerCase().includes('no longer exists')

      // If folder was deleted, mark upload as failed gracefully
      if (isFolderDeleted) {
        console.log('📊 [FOLDER_UPLOAD_PROGRESS] Folder deleted/moved during upload:', {
          folderId: payload.folderId,
          status: payloadStatus,
          error: (payload as any).error,
        })

        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
            if (f.isFolderUpload && f.folderUploadId === payload.folderId) {
              return {
                ...f,
                status: FileStatus.FAILED,
                progress: 100, // Mark as complete to remove from progress
                errorMessage: 'Destination folder was deleted or moved',
              }
            }

            return f
          }),
        }))

        // Remove from localStorage when folder fails
        removeUploadFromStorage(payload.folderId, 'folder')

        // Show error toast
        ;(async () => {
          const { toast } = await import('sonner')
          const folderName = currentFolder.serverName || currentFolder.originalName || 'Folder'

          toast.error(
            `Upload failed: "${folderName}" was deleted or moved during upload. Upload cannot continue.`,
            {
              duration: 8000,
            },
          )
        })()

        return
      }

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          // Check if this is a folder upload entry that matches the payload
          if (f.isFolderUpload && f.folderUploadId === payload.folderId) {
            // Don't update if the folder upload has been manually cancelled or already failed
            if (
              (f.status === FileStatus.FAILED && f.errorMessage?.includes('cancel')) ||
              (f.status === FileStatus.FAILED && f.errorMessage?.includes('deleted'))
            ) {
              return f
            }

            // 🎯 FRONTEND-ONLY TRACKING: Use frontend counts as source of truth
            const { progress } = payload
            const encryptionProgress = progress.encryption

            // Use frontend tracking as the source of truth for upload progress
            const totalFiles = f.totalFiles || 0
            const uploadedFiles = f.uploadedFiles || 0
            const failedUploads = f.failedUploads || 0
            const failedEncryptions = f.failedEncryptions || 0
            const encryptedFiles = f.encryptedFiles || 0

            // Only use backend data for encryption progress (not upload progress)
            const backendEncryptedFiles = encryptionProgress.completed || 0
            const backendEncryptionFailures = encryptionProgress.failed || 0

            // Use the higher count between frontend and backend for encryption
            const actualEncryptedFiles = Math.max(encryptedFiles, backendEncryptedFiles)
            const actualEncryptionFailures = Math.max(failedEncryptions, backendEncryptionFailures)

            // Calculate progress based on frontend tracking
            const totalProcessed = uploadedFiles + failedUploads
            const uploadFinished = totalProcessed >= totalFiles

            // Encryption phase is done when: encrypted + encryption_failed >= successfully_uploaded_files
            const encryptionFinished =
              actualEncryptedFiles + actualEncryptionFailures >= uploadedFiles

            // Calculate progress percentage based on frontend tracking
            const progressedFiles = Math.min(totalFiles, totalProcessed)
            const fileProgressPercentage =
              totalFiles > 0 ? Math.round((progressedFiles / totalFiles) * 100) : 0

            // For folder uploads, use the progress calculated from frontend tracking
            const newProgress = Math.max(
              f.progress || 0,
              Math.min(100, Math.max(0, fileProgressPercentage)),
            )

            // Update the folder progress entry with frontend-tracked data
            const formattedName = formatFolderProgress(f.originalName, f.serverName)

            // Determine status based on frontend tracking
            const allFilesFailed = failedUploads >= totalFiles && uploadedFiles === 0
            const allDone = uploadFinished && (encryptionFinished || uploadedFiles === 0)

            const newStatus = allFilesFailed
              ? FileStatus.FAILED
              : allDone
                ? FileStatus.AVAILABLE
                : uploadFinished
                  ? FileStatus.ENCRYPTING
                  : FileStatus.UPLOADING

            // Calculate time estimate for upload phase only
            const currentTime = Date.now()
            let timeEstimate = f.timeEstimate || ''
            let averageUploadSpeed = f.averageUploadSpeed

            // Calculate time estimate during upload phase OR during encryption if upload is still in progress
            const isUploadingPhase =
              newStatus === FileStatus.UPLOADING ||
              (newStatus === FileStatus.ENCRYPTING && uploadedFiles < totalFiles)

            if (isUploadingPhase && f.uploadStartTime && f.totalBytes && f.totalFiles) {
              // Update every 2 seconds or on first update, or if we have significant progress
              const timeSinceLastUpdate = f.lastEstimateUpdate
                ? currentTime - f.lastEstimateUpdate
                : 0
              const hasSignificantProgress = uploadedFiles > (f.uploadedFiles || 0)
              const shouldUpdateEstimate =
                !f.lastEstimateUpdate || timeSinceLastUpdate >= 2000 || hasSignificantProgress

              if (shouldUpdateEstimate) {
                const elapsedTime = (currentTime - f.uploadStartTime) / 1000 // seconds

                if (elapsedTime > 0 && uploadedFiles > 0) {
                  // Calculate uploaded bytes based on completed files and average file size
                  const averageFileSize = f.totalBytes / f.totalFiles
                  const uploadedBytes = uploadedFiles * averageFileSize
                  const remainingBytes = f.totalBytes - uploadedBytes
                  const remainingFiles = totalFiles - uploadedFiles

                  // Calculate current speed
                  let currentSpeed = uploadedBytes / elapsedTime // bytes per second

                  // Account for warmup period: First few files might upload slower
                  // Apply a correction factor if we're early in the upload
                  const completionRatio = uploadedFiles / totalFiles

                  if (completionRatio < 0.1 && elapsedTime < 30) {
                    // In the first 10% of files or first 30 seconds, apply a slight boost
                    // This accounts for connection warmup and helps avoid overly pessimistic estimates
                    currentSpeed = currentSpeed * 1.15
                  }

                  // Update average speed with exponential moving average
                  averageUploadSpeed = updateAverageSpeed(currentSpeed, f.averageUploadSpeed, 0.4)

                  // For more accurate estimates, also consider file-based rate
                  // This helps when file sizes vary significantly
                  const filesPerSecond = uploadedFiles / elapsedTime
                  const fileBasedEstimate = remainingFiles / filesPerSecond

                  // Calculate byte-based estimate
                  const effectiveSpeed = Math.max(averageUploadSpeed, 50 * 1024) // Minimum 50 KB/s
                  const byteBasedEstimate = remainingBytes / effectiveSpeed

                  // Use weighted average of both estimates for better accuracy
                  // Favor byte-based for large files, file-based for many small files
                  const avgFileSize = f.totalBytes / f.totalFiles
                  const byteWeight = avgFileSize > 1024 * 1024 ? 0.7 : 0.5 // More weight for larger files
                  const estimatedSeconds =
                    byteBasedEstimate * byteWeight + fileBasedEstimate * (1 - byteWeight)

                  timeEstimate = formatTimeEstimate(estimatedSeconds)
                } else if (uploadedFiles === 0 && elapsedTime >= 3) {
                  // Show conservative estimate after 3 seconds if no files completed yet
                  // Use slower conservative speed (500 KB/s) to avoid underestimating time
                  // This prevents showing optimistic estimate that jumps to higher value later
                  const conservativeSpeed = 512 * 1024 // 500 KB/s in bytes
                  const estimatedSeconds = f.totalBytes / conservativeSpeed

                  timeEstimate = formatTimeEstimate(estimatedSeconds)
                } else {
                  // If we don't have enough data yet, use a simple file-based estimate
                  if (uploadedFiles > 0 && f.totalFiles > 0) {
                    const filesPerSecond = uploadedFiles / elapsedTime
                    const remainingFiles = f.totalFiles - uploadedFiles
                    const estimatedSeconds = remainingFiles / filesPerSecond

                    timeEstimate = formatTimeEstimate(estimatedSeconds)
                  }
                }
              } else {
                // Keep existing estimate if not time to update
                timeEstimate = f.timeEstimate || ''
                averageUploadSpeed = f.averageUploadSpeed
              }
            } else if (newStatus === FileStatus.AVAILABLE) {
              // Only clear time estimate when upload is completely finished
              timeEstimate = ''
            } else if (newStatus === FileStatus.ENCRYPTING) {
              // Keep time estimate during encryption phase if upload is still in progress
              // Only clear if all files are uploaded (uploadedFiles === totalFiles)
              if (uploadedFiles >= totalFiles) {
                timeEstimate = ''
              } else {
                // Keep existing estimate during mixed upload/encryption phase
                timeEstimate = f.timeEstimate || ''
              }
            }

            return {
              ...f,
              name: formattedName,
              // Keep frontend counts as source of truth
              uploadedFiles: uploadedFiles,
              totalFiles: totalFiles,
              failedUploads: failedUploads,
              failedEncryptions: actualEncryptionFailures,
              encryptedFiles: actualEncryptedFiles,
              failedFiles: failedUploads + actualEncryptionFailures,
              progress: newProgress,
              status: newStatus,
              timeEstimate,
              lastEstimateUpdate: currentTime,
              averageUploadSpeed,
            }
          }

          return f
        }),
      }))

      // After updating state, check for status transitions
      const updatedFolder = get().uploadingFiles.find(
        f => f.isFolderUpload && f.folderUploadId === payload.folderId,
      )

      // Check if upload phase is complete using folder's own tracking data
      // Similar to how uploadFinished is calculated: totalProcessed >= totalFiles
      // where totalProcessed = uploadedFiles + failedUploads
      if (updatedFolder) {
        const totalFiles = updatedFolder.totalFiles || 0
        const uploadedFiles = updatedFolder.uploadedFiles || 0
        const failedUploads = updatedFolder.failedUploads || 0
        const totalProcessed = uploadedFiles + failedUploads
        const uploadFinished = totalProcessed >= totalFiles

        // Remove from localStorage when upload phase is complete (all files uploaded or failed)
        // This matches the logic where cancel button is hidden (progressCount === 0)
        if (uploadFinished && totalFiles > 0) {
          // Check if folder was already removed (avoid duplicate removal)
          const stored = localStorage.getItem(UPLOAD_STORAGE_KEY)

          if (stored) {
            const uploads: StoredUpload[] = JSON.parse(stored)
            const exists = uploads.some(u => u.id === payload.folderId && u.type === 'folder')

            if (exists) {
              removeUploadFromStorage(payload.folderId, 'folder')
            }
          }
        }
      }

      // 🚀 UPDATE INDIVIDUAL FILES STATUS: When folder transitions to ENCRYPTING, update individual files
      if (
        updatedFolder &&
        updatedFolder.status === FileStatus.ENCRYPTING &&
        previousStatus === FileStatus.UPLOADING
      ) {
        // Folder just entered encryption phase - update individual files to show ENCRYPTING status
        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
            // Update all individual files that belong to this folder and are still uploading
            if (
              f.folderUploadId === payload.folderId &&
              !f.isFolderUpload &&
              f.status === FileStatus.UPLOADING
            ) {
              return {
                ...f,
                status: FileStatus.ENCRYPTING,
              }
            }

            return f
          }),
        }))

        // Update cache to show individual files as ENCRYPTING
        if (_queryClient) {
          ;(async () => {
            const { queryKeys } = await import('@qse/api')

            const allFileQueries = _queryClient.getQueriesData({
              queryKey: queryKeys.files.lists(),
            })

            allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
              if (!cacheData) return

              const updateFileStatusInCache = (oldData: any) => {
                if (!oldData) return oldData

                if (oldData.pages) {
                  return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                      ...page,
                      data: (page.data || []).map((f: any) => {
                        const belongsToFolder =
                          updatedFolder.pathToId &&
                          Array.from(updatedFolder.pathToId.values()).includes(f.parentId)

                        if (belongsToFolder && f.status === 'UPLOADING') {
                          return { ...f, status: 'ENCRYPTING' }
                        }

                        return f
                      }),
                    })),
                  }
                }

                if (oldData.data) {
                  return {
                    ...oldData,
                    data: (oldData.data || []).map((f: any) => {
                      const belongsToFolder =
                        updatedFolder.pathToId &&
                        Array.from(updatedFolder.pathToId.values()).includes(f.parentId)

                      if (belongsToFolder && f.status === 'UPLOADING') {
                        return { ...f, status: 'ENCRYPTING' }
                      }

                      return f
                    }),
                  }
                }

                return oldData
              }

              _queryClient.setQueryData(queryKey, (oldData: any) =>
                updateFileStatusInCache(oldData),
              )
            })
          })()
        }
      }

      // Check if folder just completed (transitioned to AVAILABLE)
      if (
        updatedFolder &&
        updatedFolder.status === FileStatus.AVAILABLE &&
        previousStatus !== FileStatus.AVAILABLE
      ) {
        // Remove from localStorage on folder completion
        // Use payload.folderId directly as it's the source of truth from WebSocket
        removeUploadFromStorage(payload.folderId, 'folder')

        // Folder just completed - check for missing files that didn't get status updates
        const totalFiles = updatedFolder.totalFiles || 0
        const uploadedFiles = updatedFolder.uploadedFiles || 0
        let failedFiles = updatedFolder.failedFiles || 0
        const failedUploads = updatedFolder.failedUploads || 0
        let failedEncryptions = updatedFolder.failedEncryptions || 0
        const encryptedFiles = updatedFolder.encryptedFiles || 0

        // Calculate unaccounted files
        // Files that didn't get upload status = totalFiles - (uploaded + upload_failed)
        const filesWithUploadStatus = uploadedFiles + failedUploads
        const missingUploadStatus = totalFiles - filesWithUploadStatus

        // Files that didn't get encryption status = uploaded - (encrypted + encryption_failed)
        const filesWithEncryptionStatus = encryptedFiles + failedEncryptions
        const missingEncryptionStatus = uploadedFiles - filesWithEncryptionStatus

        // If we have missing files, mark them as failed
        if (missingUploadStatus > 0 || missingEncryptionStatus > 0) {
          console.warn('📊 [FOLDER_UPLOAD_PROGRESS] Missing file statuses detected:', {
            totalFiles,
            uploadedFiles,
            failedUploads,
            encryptedFiles,
            failedEncryptions,
            missingUploadStatus,
            missingEncryptionStatus,
          })

          // Add missing files to failed counts
          failedFiles += missingUploadStatus + missingEncryptionStatus
          failedEncryptions += missingEncryptionStatus

          // Update the folder in state with corrected failed counts
          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
              if (f.isFolderUpload && f.folderUploadId === payload.folderId) {
                return {
                  ...f,
                  failedFiles,
                  failedEncryptions,
                }
              }

              return f
            }),
          }))
        }

        // Call completeFolderUpload API to notify backend that upload session is finished
        // This ensures backend can free up storage space when there are failures
        // Only send when successful uploads < total files (indicates incomplete upload from backend perspective)
        if (uploadedFiles < totalFiles) {
          ;(async () => {
            try {
              const { completeFolderUpload } = await import('@qse/api')

              await completeFolderUpload(payload.folderId)
              console.log(
                '📂 [FolderUpload] Successfully completed folder upload session:',
                payload.folderId,
                `(${uploadedFiles}/${totalFiles} files uploaded)`,
              )
            } catch (error) {
              console.error('📂 [FolderUpload] Failed to complete folder upload session:', error)
              // Don't show error toast to user as this is a background operation
            }
          })()
        } else {
          console.log(
            '📂 [FolderUpload] Skipping completeFolderUpload call - all files uploaded successfully:',
            payload.folderId,
            `(${uploadedFiles}/${totalFiles} files uploaded)`,
          )
        }

        // 🚀 UPDATE INDIVIDUAL FILES: Mark all files in this folder as AVAILABLE when folder completes
        // This fixes the issue where users see files as "uploading" even when they're in encryption phase
        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
            // Update all individual files that belong to this completed folder
            if (f.folderUploadId === payload.folderId && !f.isFolderUpload) {
              return {
                ...f,
                status: FileStatus.AVAILABLE,
                progress: 100,
              }
            }

            return f
          }),
        }))

        // 🚀 UPDATE CACHE: Update React Query cache for individual files in current view
        // This ensures FileCard shows files as AVAILABLE without triggering new API requests
        if (_queryClient) {
          ;(async () => {
            const { queryKeys } = await import('@qse/api')

            // Get all file list queries in current view
            const allFileQueries = _queryClient.getQueriesData({
              queryKey: queryKeys.files.lists(),
            })

            allFileQueries.forEach(([queryKey, cacheData]: [any, any]) => {
              if (!cacheData) return

              const updateFileStatusInCache = (oldData: any) => {
                if (!oldData) return oldData

                if (oldData.pages) {
                  return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                      ...page,
                      data: (page.data || []).map((f: any) => {
                        // Check if this file belongs to the completed folder upload
                        const belongsToCompletedFolder =
                          updatedFolder.pathToId &&
                          Array.from(updatedFolder.pathToId.values()).includes(f.parentId)

                        if (
                          belongsToCompletedFolder &&
                          (f.status === 'UPLOADING' || f.status === 'ENCRYPTING')
                        ) {
                          return { ...f, status: 'AVAILABLE' }
                        }

                        return f
                      }),
                    })),
                  }
                }

                if (oldData.data) {
                  return {
                    ...oldData,
                    data: (oldData.data || []).map((f: any) => {
                      // Check if this file belongs to the completed folder upload
                      const belongsToCompletedFolder =
                        updatedFolder.pathToId &&
                        Array.from(updatedFolder.pathToId.values()).includes(f.parentId)

                      if (
                        belongsToCompletedFolder &&
                        (f.status === 'UPLOADING' || f.status === 'ENCRYPTING')
                      ) {
                        return { ...f, status: 'AVAILABLE' }
                      }

                      return f
                    }),
                  }
                }

                return oldData
              }

              _queryClient.setQueryData(queryKey, (oldData: any) =>
                updateFileStatusInCache(oldData),
              )
            })
          })()
        }

        // File explorer is updated via FOLDER_UPLOAD_PROGRESS WebSocket event handler
        // No need to update here as it's handled on every progress update
      }
    },

    updateFolderUploadProgress: (payload: any) => {
      console.log('updateFolderUploadProgress', payload)
      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          if (f.isFolderUpload && f.folderUploadId === payload.folderId) {
            // Don't update if the folder upload has been manually cancelled
            if (f.status === FileStatus.FAILED && f.errorMessage?.includes('cancel')) {
              return f
            }

            const progress = payload.progress
            const uploadProgress = progress.upload
            const encryptionProgress = progress.encryption

            // Calculate overall progress percentage
            const totalProgress = uploadProgress.total + encryptionProgress.total
            const completedProgress = uploadProgress.completed + encryptionProgress.completed
            const overallPercentage =
              totalProgress > 0 ? Math.round((completedProgress / totalProgress) * 100) : 0

            // Update the folder progress entry with real-time data
            return {
              ...f,
              progress: overallPercentage,
              uploadedFiles: uploadProgress.completed,
              failedFiles: uploadProgress.failed + encryptionProgress.failed,
              failedUploads: uploadProgress.failed,
              failedEncryptions: encryptionProgress.failed,
              name: formatFolderProgress(f.originalName, f.serverName),
              // Update status based on completion
              status:
                uploadProgress.completed === uploadProgress.total &&
                encryptionProgress.completed === encryptionProgress.total
                  ? FileStatus.AVAILABLE
                  : uploadProgress.failed > 0 || encryptionProgress.failed > 0
                    ? FileStatus.FAILED
                    : FileStatus.UPLOADING,
            }
          }

          return f
        }),
      }))
    },

    // Reset all upload state - useful for logout
    clearCompleted: () =>
      set(state => ({
        uploadingFiles: state.uploadingFiles.filter(
          f =>
            f.status === FileStatus.UPLOADING ||
            f.status === FileStatus.ENCRYPTING ||
            (f.status as string) === 'UPLOADING' ||
            (f.status as string) === 'ENCRYPTING',
        ),
      })),

    reset: () => {
      // Abort all ongoing uploads
      const { abortControllers } = get()

      abortControllers.forEach(controller => controller.abort())

      // Clear all state
      set({
        isUploadModalOpen: false,
        uploadingFiles: [],
        driveFiles: [],
        abortControllers: new Map<number, AbortController>(),
        folderAbortControllers: new Map<string, AbortController>(),
        isAborted: false,
        beforeUnloadHandler: null,
        uploadQueue: [],
        activeUploads: 0,
        deletedFolderIds: new Set<string>(),
      })
    },

    processUploadQueue: () => {
      // Process queue if we have capacity and pending uploads
      // IMPORTANT: Re-read activeUploads from state in each iteration to ensure accurate concurrency control
      while (get().activeUploads < MAX_GLOBAL_CONCURRENT_UPLOADS && get().uploadQueue.length > 0) {
        const uploadQueue = get().uploadQueue
        const uploadTask = uploadQueue.shift()

        if (uploadTask) {
          // Update queue state after shift
          set({ uploadQueue: [...uploadQueue] })

          // Increment active uploads counter
          set(state => ({ activeUploads: state.activeUploads + 1 }))

          // Execute the upload task
          uploadTask().finally(() => {
            // Decrement active uploads counter when done
            set(state => ({ activeUploads: state.activeUploads - 1 }))
            // Process next item in queue
            get().processUploadQueue()
          })
        }
      }
    },

    addDeletedFolderId: (folderId: string) => {
      set(state => {
        const newDeletedFolderIds = new Set(state.deletedFolderIds)

        newDeletedFolderIds.add(folderId)
        console.log('🗑️ [DeletedFolderTracking] Added deleted folder ID:', {
          folderId,
          totalTracked: newDeletedFolderIds.size,
        })

        return { deletedFolderIds: newDeletedFolderIds }
      })
    },

    addDeletedFolderIds: (folderIds: string[]) => {
      set(state => {
        const newDeletedFolderIds = new Set(state.deletedFolderIds)

        folderIds.forEach(id => newDeletedFolderIds.add(id))
        console.log('🗑️ [DeletedFolderTracking] Added multiple deleted folder IDs:', {
          count: folderIds.length,
          folderIds,
          totalTracked: newDeletedFolderIds.size,
        })

        return { deletedFolderIds: newDeletedFolderIds }
      })
    },

    clearDeletedFolderIds: () => {
      set({ deletedFolderIds: new Set<string>() })
      console.log('🗑️ [DeletedFolderTracking] Cleared all deleted folder IDs')
    },

    checkAndAbortAffectedFiles: async (deletedFolderIds: string[]) => {
      console.log(
        '🗑️ [DeletedFolderTracking] PROACTIVE: Checking affected files for deleted folders:',
        {
          deletedFolderIds,
          timestamp: new Date().toISOString(),
        },
      )

      const state = get()
      const deletedFolderIdsSet = new Set(deletedFolderIds)
      const mainFoldersToCancel: string[] = []
      const affectedFilesToAbort: EnhancedUploadingFile[] = []
      const affectedFilesToMarkAsFailed: EnhancedUploadingFile[] = []

      // 🚀 PROACTIVE APPROACH: Process all folder uploads, not just in-progress ones
      const folderUploads = state.uploadingFiles.filter(f => f.isFolderUpload)

      for (const folderUpload of folderUploads) {
        if (!folderUpload.folderUploadId || !folderUpload.pathToId) continue

        // 🚀 Check if the main folder itself was deleted - cancel entire upload
        if (deletedFolderIdsSet.has(folderUpload.folderUploadId)) {
          console.log(
            '🗑️ [DeletedFolderTracking] PROACTIVE: Main folder deleted, cancelling entire upload:',
            {
              folderUploadId: folderUpload.folderUploadId,
              folderName: folderUpload.name,
            },
          )
          mainFoldersToCancel.push(folderUpload.folderUploadId)
          continue
        }

        // 🚀 PROACTIVE: Check ALL files in this folder upload (not just in-progress ones)
        const allFilesInFolder = state.uploadingFiles.filter(
          f => f.isFolderUpload && f.folderUploadId === folderUpload.folderUploadId,
        )

        for (const file of allFilesInFolder) {
          if (!file.parentId) continue

          // 🚀 SIMPLIFIED: Check if file's destination is deleted (direct or nested)
          const isFileDestinationDeleted = (fileParentId: string): boolean => {
            // Direct match
            if (deletedFolderIdsSet.has(fileParentId)) return true

            // Check if file's parent is a descendant of any deleted folder
            // Use the pathToId mapping to check parent relationships
            for (const [path, id] of folderUpload.pathToId!.entries()) {
              if (id === fileParentId) {
                // Check if this path is under any deleted folder
                for (const deletedFolderId of deletedFolderIds) {
                  const deletedPath = Array.from(folderUpload.pathToId!.entries()).find(
                    ([, deletedId]) => deletedId === deletedFolderId,
                  )?.[0]

                  if (deletedPath && (path.startsWith(deletedPath + '/') || path === deletedPath)) {
                    return true
                  }
                }
              }
            }

            return false
          }

          if (isFileDestinationDeleted(file.parentId)) {
            console.log('🗑️ [DeletedFolderTracking] PROACTIVE: File destination deleted:', {
              fileName: file.name,
              fileParentId: file.parentId,
              folderUploadId: folderUpload.folderUploadId,
            })

            // Check if file is currently being uploaded (has abort controller)
            const hasAbortController = state.abortControllers.has(file.id)

            if (hasAbortController) {
              // File is currently uploading - abort it
              affectedFilesToAbort.push(file)
            } else {
              // File hasn't started uploading yet - mark as failed proactively
              affectedFilesToMarkAsFailed.push(file)
            }
          }
        }
      }

      console.log('🗑️ [DeletedFolderTracking] PROACTIVE: Found affected files:', {
        mainFoldersToCancel: mainFoldersToCancel.length,
        filesToAbort: affectedFilesToAbort.length,
        filesToMarkAsFailed: affectedFilesToMarkAsFailed.length,
        totalAffected: affectedFilesToAbort.length + affectedFilesToMarkAsFailed.length,
      })

      // 🚀 PROACTIVE: Handle main folder deletions - cancel entire uploads
      if (mainFoldersToCancel.length > 0) {
        try {
          // Import the cancelFolderUpload function (same as in handleAbortAll)
          const { cancelFolderUpload } = await import('@qse/api')

          const folderCancelPromises = mainFoldersToCancel.map(async folderId => {
            try {
              console.log(
                `🗑️ [DeletedFolderTracking] PROACTIVE: Cancelling folder upload: ${folderId}`,
              )
              await cancelFolderUpload(folderId)
              console.log(
                `🗑️ [DeletedFolderTracking] PROACTIVE: Successfully cancelled: ${folderId}`,
              )
            } catch (error) {
              console.error(
                `🗑️ [DeletedFolderTracking] PROACTIVE: Failed to cancel: ${folderId}`,
                error,
              )
            } finally {
              removeUploadFromStorage(folderId, 'folder')
            }
          })

          await Promise.all(folderCancelPromises)

          // Clean up state for cancelled main folders
          set((state: GlobalFileUploadState) => ({
            uploadingFiles: state.uploadingFiles.map(f => {
              if (f.isFolderUpload && mainFoldersToCancel.includes(f.folderUploadId || '')) {
                // If folder is already completed, just remove the URL (folderUploadId) to prevent navigation
                // Keep the status and message as they are
                if (f.status === FileStatus.AVAILABLE) {
                  return {
                    ...f,
                    folderUploadId: undefined,
                  }
                }

                // If folder is not completed, mark it as failed
                return {
                  ...f,
                  status: FileStatus.FAILED,
                  errorMessage: 'Destination folder was deleted during upload',
                  progress: 100,
                }
              }

              return f
            }),
            folderAbortControllers: new Map(
              Array.from(state.folderAbortControllers.entries()).filter(
                ([folderId]) => !mainFoldersToCancel.includes(folderId),
              ),
            ),
            abortControllers: new Map(
              Array.from(state.abortControllers.entries()).filter(([fileId]) => {
                const fileObj = state.uploadingFiles.find(f => f.id === fileId)

                return !(
                  fileObj &&
                  fileObj.folderUploadId &&
                  mainFoldersToCancel.includes(fileObj.folderUploadId)
                )
              }),
            ),
          }))
        } catch (error) {
          console.error(
            '🗑️ [DeletedFolderTracking] PROACTIVE: Failed to cancel main folders:',
            error,
          )
        }
      }

      // 🚀 PROACTIVE: Abort currently uploading files to deleted destinations
      for (const file of affectedFilesToAbort) {
        const controller = state.abortControllers.get(file.id)

        if (controller) {
          console.log('🗑️ [DeletedFolderTracking] PROACTIVE: Aborting file:', file.name)
          controller.abort()
        }

        // Mark file as failed
        const failedFileDetail: FailedFileDetail = {
          name: file.name,
          path: file.path || file.name,
          reason: 'deleted_destination',
        }

        if (file.serverFileId) {
          removeUploadFromStorage(file.serverFileId, 'file')
        }

        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
            if (f.isFolderUpload && f.folderUploadId === file.folderUploadId) {
              const newFailedUploads = (f.failedUploads || 0) + 1
              const newFailedFiles = (f.failedFiles || 0) + 1

              return {
                ...f,
                failedUploads: newFailedUploads,
                failedFiles: newFailedFiles,
                failedFileDetails: addOrUpdateFailedFileDetail(
                  f.failedFileDetails,
                  failedFileDetail,
                ),
              }
            }

            return f
          }),
        }))
      }

      // 🚀 PROACTIVE: Mark files that haven't started uploading as failed
      for (const file of affectedFilesToMarkAsFailed) {
        console.log(
          '🗑️ [DeletedFolderTracking] PROACTIVE: Marking file as failed before upload:',
          file.name,
        )

        const failedFileDetail: FailedFileDetail = {
          name: file.name,
          path: file.path || file.name,
          reason: 'deleted_destination',
        }

        if (file.serverFileId) {
          removeUploadFromStorage(file.serverFileId, 'file')
        }

        set((state: GlobalFileUploadState) => ({
          uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
            if (f.isFolderUpload && f.folderUploadId === file.folderUploadId) {
              const newFailedUploads = (f.failedUploads || 0) + 1
              const newFailedFiles = (f.failedFiles || 0) + 1

              return {
                ...f,
                failedUploads: newFailedUploads,
                failedFiles: newFailedFiles,
                failedFileDetails: addOrUpdateFailedFileDetail(
                  f.failedFileDetails,
                  failedFileDetail,
                ),
              }
            }

            return f
          }),
        }))
      }

      // Add deleted folder IDs to tracking set
      state.addDeletedFolderIds(deletedFolderIds)
    },

    recalculateFolderProgress: (folderUploadId: string) => {
      console.log('🔄 [FrontendTracking] Recalculating folder progress:', folderUploadId)

      set((state: GlobalFileUploadState) => ({
        uploadingFiles: state.uploadingFiles.map((f: EnhancedUploadingFile) => {
          if (f.isFolderUpload && f.folderUploadId === folderUploadId) {
            const totalFiles = f.totalFiles || 0
            const uploadedFiles = f.uploadedFiles || 0
            const failedUploads = f.failedUploads || 0
            const failedEncryptions = f.failedEncryptions || 0
            const encryptedFiles = f.encryptedFiles || 0

            // Calculate progress based on frontend tracking
            const totalProcessed = uploadedFiles + failedUploads
            const uploadFinished = totalProcessed >= totalFiles
            const encryptionFinished = encryptedFiles + failedEncryptions >= uploadedFiles

            // Calculate progress percentage
            const progressPercentage =
              totalFiles > 0 ? Math.round((totalProcessed / totalFiles) * 100) : 0

            // Determine status
            const allFilesFailed = failedUploads >= totalFiles && uploadedFiles === 0
            const allDone = uploadFinished && (encryptionFinished || uploadedFiles === 0)

            const newStatus = allFilesFailed
              ? FileStatus.FAILED
              : allDone
                ? FileStatus.AVAILABLE
                : uploadFinished
                  ? FileStatus.ENCRYPTING
                  : FileStatus.UPLOADING

            console.log('🔄 [FrontendTracking] Recalculated progress:', {
              folderUploadId,
              totalFiles,
              uploadedFiles,
              failedUploads,
              failedEncryptions,
              encryptedFiles,
              progressPercentage,
              newStatus,
            })

            return {
              ...f,
              progress: progressPercentage,
              status: newStatus,
              failedFiles: failedUploads + failedEncryptions,
            }
          }

          return f
        }),
      }))
    },

    getCurrentlyUploadingFolderIds: (): string[] => {
      const state = get()
      const uploadingFolderIds = new Set<string>()

      state.uploadingFiles.forEach(file => {
        if (file.isFolderUpload && file.folderUploadId) {
          uploadingFolderIds.add(file.folderUploadId)

          if (file.pathToId) {
            Array.from(file.pathToId.values()).forEach(id => {
              uploadingFolderIds.add(id)
            })
          }
        }
      })

      return Array.from(uploadingFolderIds)
    },
    checkAndCancelStaleUploads: async (queryClient?: any) => {
      // Ensure BroadcastChannel listener is set up (already set up on store creation, but keep as safety measure)
      setupBroadcastChannelListener(get, set)

      // Check and cancel stale uploads
      await checkAndCancelStaleUploads(get, queryClient)
    },
  }
})

// Global reset function that can be called from anywhere
export const resetGlobalFileUpload = () => {
  const store = useGlobalFileUpload.getState()

  store.reset()
}

// Global logout abort function that can be called from anywhere
// This gracefully cancels all uploads when user logs out
export const handleLogoutUploadAbort = async (queryClient?: any) => {
  const store = useGlobalFileUpload.getState()

  await store.handleLogoutAbort(queryClient)
}
